import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  MenuItem,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Divider,
  CircularProgress,
  Tabs,
  Tab,
  Avatar,
  Link,
  Chip,
  Tooltip,
  Menu as MuiMenu,
  Checkbox,
  FormControlLabel,
  ListSubheader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  RadioGroup,
  Radio,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  LinearProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  Print,
  CheckCircle,
  Visibility,
  Delete,
  Tune,
  Add,
  CloudUpload,
  Close,
  Search,
  Settings,
  PlayArrow,
  AttachFile,
  PersonAdd,
  KeyboardArrowDown,
  KeyboardArrowRight,
  Menu as MenuIcon,
  FileCopy,
  Link as LinkIcon,
  Email,
  MarkEmailRead,
  Share,
  FolderOpen,
  PictureAsPdf,
  Description,
  LocalShipping,
  MoreVert,
  ExpandMore,
  ExpandLess,
  NavigateBefore,
  NavigateNext,
  Person,
  Business,
  OpenInFull,
  HelpOutline,
  Phone,
  StarBorder,
  AddPhotoAlternate,
  Inventory,
  LocationOn,
} from '@mui/icons-material';
import { quotationAPI, customerAPI, productAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import useDebounce from '../../utils/useDebounce';
import EditableRowInput from '../../components/common/EditableRowInput';
import { SalespersonSelect } from '../../components/common';

const countryOptions = ['India', 'United Kingdom (UK)'];

const initialLineItem = {
  product: '',
  productName: '',
  description: '',
  quantity: 1,
  unitPrice: 0,
  discount: 0,
  discountType: 'percentage',
  taxRate: 18,
};

const QuotationBuilder = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerInputValue, setCustomerInputValue] = useState('');
  const [cityOptions, setCityOptions] = useState(['Gurugram', 'Mumbai', 'Bangalore']);
  const [products, setProducts] = useState([]);
  const [customProducts, setCustomProducts] = useState(() => {
    // Load custom products from localStorage on initial render
    const saved = localStorage.getItem('customProducts');
    if (saved) {
      // Filter out products with "custom" category (cleanup old data)
      const parsed = JSON.parse(saved);
      const filtered = parsed.filter(p => p.category && p.category.toLowerCase() !== 'custom');
      // Update localStorage if we filtered anything
      if (filtered.length !== parsed.length) {
        localStorage.setItem('customProducts', JSON.stringify(filtered));
      }
      return filtered;
    }
    return [];
  });
  const [activeTab, setActiveTab] = useState(0);
  const [status, setStatus] = useState('draft');
  const [activityTab, setActivityTab] = useState(0); // 0 = Send message, 1 = Log note, 2 = Activity
  const [expandedDescriptions, setExpandedDescriptions] = useState({}); // Track which rows have description expanded
  const [itemErrors, setItemErrors] = useState({}); // Track validation errors for each item
  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null); // Anchor for column visibility menu
  const [settingsMenuAnchor, setSettingsMenuAnchor] = useState(null); // Anchor for settings menu
  const [printSubmenuAnchor, setPrintSubmenuAnchor] = useState(null); // Anchor for print submenu
  const [customerError, setCustomerError] = useState(false); // Track customer selection error
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null); // Anchor for responsive action menu
  const [collapsedCategories, setCollapsedCategories] = useState({}); // Track collapsed product categories
  const [productSearchText, setProductSearchText] = useState(''); // Track search text for product dropdown
  const debouncedProductSearch = useDebounce(productSearchText, 300);
  const [categorySearchActive, setCategorySearchActive] = useState({}); // Track which category has search active
  const [categorySearchText, setCategorySearchText] = useState({}); // Track search text per category
  const [keepDropdownOpen, setKeepDropdownOpen] = useState(false); // Prevent dropdown from closing
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null); // Track which dropdown is open
  const preventCloseRef = React.useRef(false); // Use ref to prevent close without re-render

  // Attachments
  const [attachments, setAttachments] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]); // Files queued before quotation is saved
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = React.useRef(null);

  // Custom Payment Term Dialog
  const [paymentTermDialogOpen, setPaymentTermDialogOpen] = useState(false);
  const [paymentTermSearchDialogOpen, setPaymentTermSearchDialogOpen] = useState(false);
  const [paymentTermSearchText, setPaymentTermSearchText] = useState('');
  const [customPaymentTerms, setCustomPaymentTerms] = useState(() => {
    // Load custom payment terms from localStorage on initial render
    const saved = localStorage.getItem('customPaymentTerms');
    return saved ? JSON.parse(saved) : [];
  });
  const [newPaymentTerm, setNewPaymentTerm] = useState({ name: '', days: '', description: '' });
  const [paymentTermPage, setPaymentTermPage] = useState(0);
  const paymentTermsPerPage = 10; // Show 5 items per page for easier pagination
  
  // Save custom payment terms to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('customPaymentTerms', JSON.stringify(customPaymentTerms));
  }, [customPaymentTerms]);
  
  // Predefined payment terms
  const predefinedPaymentTerms = [
    { id: 'immediate', name: 'Immediate Payment', days: 0 },
    { id: '15_days', name: '15 Days', days: 15 },
    { id: '21_days', name: '21 Days', days: 21 },
    { id: '30_days', name: '30 Days', days: 30 },
    { id: '45_days', name: '45 Days', days: 45 },
    { id: 'end_following_month', name: 'End of Following Month', days: null },
    { id: '10_days_end_next_month', name: '10 Days after End of Next Month', days: null },
    { id: '30_now_balance_60', name: '30% Now, Balance 60 Days', days: 60 },
    { id: '2_7_net_30', name: '2/7 Net 30', days: 30 },
    { id: '90_days_on_10th', name: '90 days, on the 10th', days: 90 },
  ];
  
  // All payment terms (predefined + custom)
  const allPaymentTerms = [...predefinedPaymentTerms, ...customPaymentTerms];
  
  // Custom Product Dialog
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    productType: 'goods',
    canBeSold: true,
    canBePurchased: true,
    isRental: false,
    trackInventory: false,
    invoicingPolicy: 'ordered',
    salesPrice: '',
    salesTax: '18',
    cost: '',
    purchaseTax: '18',
    category: '',
    reference: '',
    barcode: '',
    hsnCode: '',
    company: 'all',
    internalNotes: '',
  });
  const [productDialogIndex, setProductDialogIndex] = useState(null);
  const [productTab, setProductTab] = useState(0);
  
  // Custom Customer Dialog
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    type: 'person',
    name: '',
    email: '',
    phone: '',
    company: '',
    street: '',
    city: '',
    zip: '',
    state: '',
    country: 'India',
    gstin: '',
  });
  const [customerTab, setCustomerTab] = useState(0);
  
  const [visibleColumns, setVisibleColumns] = useState({
    srNo: true,
    productVariant: false,
    product: true,
    description: true,
    leadTime: false,
    cost: false,
    taxes: true,
    taxIncl: false,
  });

  const [formData, setFormData] = useState({
    title: '',
    customer: null,
    quotationDate: dayjs(),
    validUntil: dayjs().add(30, 'day'),
    paymentTerms: 'immediate',
    quotationTemplate: 'T4LQuote',
    pricelist: 'default_inr',
    items: [],
    optionalProducts: [],
    additionalDiscount: 0,
    additionalDiscountType: 'percentage',
    shippingCharges: 0,
    termsAndConditions: `* Delivery – 4-5 working days after confirmation.
* Delivery: License will be in from of ESD – Electronic Software Delivery (Software to be downloaded from web link)
* All cloud licenses are subscription based hence needs to be renewed after 12 months
* All the Licenses billed are E Licenses and do not come with Media.
* Prices quoted for software licenses do not include any services like installation training services/implementation/installation.
* No downgrade will be possible during the 12 months subscription.
* Orders once placed cannot be cancelled under any circumstances.
* Installation chargers extra as per actual.`,
    customerNotes: '',
    internalNotes: '',
    // Sales Info
    salesperson: '',
    salesTeam: 'Sales',
    company: 'Tech 4 Logic Private Limited',
    onlineSignature: true,
    onlinePayment: true,
    onlinePaymentPercent: 100,
    customerReference: '',
    tags: [],
    // Invoicing
    fiscalPosition: 'Within Maharashtra',
    taxType: 'igst', // 'igst' or 'gst' (CGST + SGST)
    project: '',
    // Delivery
    shippingWeight: 0,
    warehouse: 'Tech 4 Logic Pvt Ltd',
    incoterm: '',
    incotermLocation: '',
    shippingPolicy: 'as_soon_as_possible',
    deliveryDate: null,
    // Tracking
    sourceDocument: '',
    opportunity: '',
    campaign: '',
    medium: '',
    source: '',
  });

  useEffect(() => {
    fetchCustomers();
    fetchProducts();
    fetchCityOptions();
    if (isEdit) {
      fetchQuotation();
    }
  }, [id]);

  // Auto-select the current user as the default salesperson when creating a
  // new quotation. Only fires when no salesperson is set yet, so it never
  // overrides a manual selection or a loaded value during edit.
  useEffect(() => {
    if (!isEdit && user?._id && !formData.salesperson) {
      setFormData(prev => ({ ...prev, salesperson: user._id }));
    }
  }, [isEdit, user, formData.salesperson]);

  // Load all products on component mount and keep full list
  // Search filtering is done client-side in the Autocomplete, not server-side
  useEffect(() => {
    fetchProducts(); // Get all products without search filter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await customerAPI.getAllForDropdown();
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchCityOptions = async () => {
    try {
      const response = await quotationAPI.getLocations();
      const list = response.data?.locations || [];
      if (list.length > 0) setCityOptions(list);
    } catch (error) {
      console.error('Error fetching city options:', error);
    }
  };

  const fetchProducts = async (search) => {
    try {
      const response = await productAPI.getAll({
        search: search || undefined,
        limit: 10000, // Load all products (increased from 100) for client-side filtering
      });
      // Drop any legacy local-only "custom_*" products from previous versions.
      // They have invalid ObjectIds and cannot be saved with a quotation.
      localStorage.removeItem('customProducts');
      setCustomProducts([]);
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchQuotation = async () => {
    try {
      setLoading(true);
      const response = await quotationAPI.getById(id);
      const quotation = response.data;

      setFormData({
        title: quotation.title,
        customer: quotation.customer,
        quotationNumber: quotation.quotationNumber || '',
        quotationDate: dayjs(quotation.quotationDate),
        validUntil: dayjs(quotation.validUntil),
        paymentTerms: quotation.paymentTerms,
        quotationTemplate: quotation.quotationTemplate || 'T4LQuote',
        pricelist: quotation.pricelist || '',
        items: quotation.items.map((item) => ({
          product: item.product?._id || item.product,
          productName: item.productName,
          description: item.description || '',
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          discount: item.discount,
          discountType: item.discountType || 'percentage',
          taxRate: item.taxRate,
        })),
        optionalProducts: quotation.optionalProducts || [],
        additionalDiscount: quotation.additionalDiscount || 0,
        additionalDiscountType: quotation.additionalDiscountType || 'percentage',
        shippingCharges: quotation.shippingCharges || 0,
        termsAndConditions: quotation.termsAndConditions || '',
        customerNotes: quotation.customerNotes || '',
        internalNotes: quotation.internalNotes || '',
        salesperson: quotation.salesPerson?._id || quotation.salesPerson || '',
      });
      setStatus(quotation.status || 'draft');
      setAttachments(Array.isArray(quotation.attachments) ? quotation.attachments : []);
    } catch (error) {
      toast.error('Failed to load quotation');
      navigate('/quotations');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCustomerChange = (event, newValue) => {
    setFormData({ ...formData, customer: newValue });
    if (newValue) {
      setCustomerError(false); // Clear error when customer is selected
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;

    if (field === 'product' && value) {
      const product = products.find((p) => p._id === value);
      if (product) {
        newItems[index].productName = product.name;
        // Don't auto-fill unitPrice - let user enter manually
        // newItems[index].unitPrice = product.unitPrice || 0;
        newItems[index].taxRate = product.taxRate !== undefined && product.taxRate !== null ? product.taxRate : 18;
      }
      // Clear error when product is selected
      setItemErrors(prev => ({ ...prev, [index]: false }));
    }

    setFormData({ ...formData, items: newItems });
  };

  const addLineItem = () => {
    // Check if there are existing items and validate the last one
    const productItems = formData.items.filter(item => !item.isSection && !item.isNote);
    if (productItems.length > 0) {
      const lastProductIndex = formData.items.findIndex(
        (item, idx) => !item.isSection && !item.isNote && 
        formData.items.slice(idx + 1).every(i => i.isSection || i.isNote)
      );
      const actualLastIndex = formData.items.length - 1 - [...formData.items].reverse().findIndex(item => !item.isSection && !item.isNote);
      const lastItem = formData.items[actualLastIndex];
      
      if (lastItem && !lastItem.product) {
        setItemErrors(prev => ({ ...prev, [actualLastIndex]: true }));
        // Auto-clear error after 3 seconds
        setTimeout(() => {
          setItemErrors(prev => ({ ...prev, [actualLastIndex]: false }));
        }, 3000);
        return;
      }
    }
    
    setFormData({
      ...formData,
      items: [...formData.items, { ...initialLineItem }],
    });
  };

  // Helper function to check customer selection
  const requireCustomer = (action) => {
    if (!formData.customer) {
      setCustomerError(true);
      toast.error('Please select a customer first');
      return false;
    }
    return true;
  };

  const addSection = () => {
    if (!requireCustomer('section')) return;
    setFormData({
      ...formData,
      items: [...formData.items, { isSection: true, sectionName: '' }],
    });
  };

  const addNote = () => {
    if (!requireCustomer('note')) return;
    setFormData({
      ...formData,
      items: [...formData.items, { isNote: true, noteText: '' }],
    });
  };

  const handleCatalogClick = () => {
    if (!requireCustomer('catalog')) return;
    navigate('/products');
  };

  const removeLineItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const calculateLineTotal = (item) => {
    if (item.isSection || item.isNote) return 0;
    const qty = parseFloat(item.quantity) || 1;
    const price = parseFloat(item.unitPrice) || 0;
    const tax = item.taxRate !== undefined && item.taxRate !== null && item.taxRate !== '' ? parseFloat(item.taxRate) : 18;
    const subtotal = qty * price;
    let discountAmount = 0;
    if (item.discountType === 'percentage') {
      discountAmount = (subtotal * (item.discount || 0)) / 100;
    } else {
      discountAmount = item.discount || 0;
    }
    const taxableAmount = subtotal - discountAmount;
    const taxAmount = (taxableAmount * tax) / 100;
    return taxableAmount + taxAmount;
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let totalTax = 0;

    formData.items.forEach((item) => {
      if (item.isSection || item.isNote) return;
      const qty = parseFloat(item.quantity) || 1;
      const price = parseFloat(item.unitPrice) || 0;
      const tax = item.taxRate !== undefined && item.taxRate !== null && item.taxRate !== '' ? parseFloat(item.taxRate) : 18;
      const itemSubtotal = qty * price;
      subtotal += itemSubtotal;

      let discountAmount = 0;
      if (item.discountType === 'percentage') {
        discountAmount = (itemSubtotal * (item.discount || 0)) / 100;
      } else {
        discountAmount = item.discount || 0;
      }

      const taxableAmount = itemSubtotal - discountAmount;
      totalTax += (taxableAmount * tax) / 100;
    });

    const grandTotal = subtotal + totalTax;

    // Calculate tax breakdown based on tax type
    let igst = 0;
    let cgst = 0;
    let sgst = 0;
    
    if (formData.taxType === 'gst') {
      // GST splits into CGST (50%) and SGST (50%)
      cgst = totalTax / 2;
      sgst = totalTax / 2;
    } else {
      // IGST is the full tax amount
      igst = totalTax;
    }

    return { subtotal, totalTax, grandTotal, igst, cgst, sgst };
  };

  const handleSubmit = async (e, { skipNavigation = false } = {}) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();

    if (!formData.customer) {
      setCustomerError(true);
      toast.error('Please select a customer');
      return null;
    }

    const validItems = formData.items.filter((item) => !item.isSection && !item.isNote);
    if (validItems.length === 0) {
      toast.error('Please add at least one product');
      return null;
    }
    
    if (validItems.some((item) => !item.product || !item.productName)) {
      toast.error('Please fill in all product details');
      return null;
    }

    // Guard against legacy local-only products (id like "custom_<timestamp>") which
    // are not valid MongoDB ObjectIds and will be rejected by the server.
    const isValidObjectId = (v) => typeof v === 'string' && /^[a-fA-F0-9]{24}$/.test(v);
    if (validItems.some((item) => !isValidObjectId(item.product))) {
      toast.error('One or more products are not saved. Please re-select or recreate them.');
      return null;
    }

    try {
      setSaving(true);
      
      // Auto-generate title if empty
      const title = formData.title || `Quotation for ${formData.customer.name || formData.customer.company || 'Customer'}`;
      
      const dataToSave = {
        ...formData,
        title,
        customer: formData.customer._id,
        quotationDate: formData.quotationDate.toISOString(),
        validUntil: formData.validUntil.toISOString(),
        status: status,
        items: formData.items
          .filter((item) => !item.isSection && !item.isNote)
          .map((item) => ({
            ...item,
            quantity: parseFloat(item.quantity) || 1,
            unitPrice: parseFloat(item.unitPrice) || 0,
            discount: parseFloat(item.discount) || 0,
            taxRate: parseFloat(item.taxRate) || 18,
          })),
        additionalDiscount: parseFloat(formData.additionalDiscount) || 0,
        shippingCharges: parseFloat(formData.shippingCharges) || 0,
      };

      let saved;
      if (isEdit) {
        const res = await quotationAPI.update(id, dataToSave);
        saved = res.data?.quotation || res.data;
        toast.success('Quotation updated successfully');
      } else {
        const response = await quotationAPI.create(dataToSave);
        saved = response.data?.quotation || response.data;
        toast.success('Quotation created successfully');
      }

      // Upload any files that were queued before the quotation existed
      if (saved?._id && pendingFiles.length > 0) {
        const uploaded = await uploadFilesToQuotation(
          saved._id,
          pendingFiles.map((p) => p.file)
        );
        if (uploaded) setPendingFiles([]);
      }

      if (!isEdit && !skipNavigation) {
        navigate(`/quotations/${saved._id}`);
        return saved;
      }
      if (!skipNavigation) navigate('/quotations');
      return saved;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save quotation');
      return null;
    } finally {
      setSaving(false);
    }
  };

  // ===== Attachments =====
  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const getFileIcon = (mimeType = '') => {
    if (mimeType.startsWith('image/')) return <AddPhotoAlternate sx={{ color: '#017e84' }} />;
    if (mimeType === 'application/pdf') return <PictureAsPdf sx={{ color: '#d32f2f' }} />;
    return <Description sx={{ color: '#666' }} />;
  };

  const handleAttachmentFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    const oversized = files.find((f) => f.size > 25 * 1024 * 1024);
    if (oversized) {
      toast.error(`"${oversized.name}" exceeds the 25 MB limit`);
      return;
    }

    // No quotation yet → queue files locally, they upload right after Save
    if (!id) {
      setPendingFiles((prev) => [
        ...prev,
        ...files.map((file) => ({
          localId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          file,
          name: file.name,
          size: file.size,
          type: file.type,
        })),
      ]);
      toast.info(`${files.length} file${files.length > 1 ? 's' : ''} queued — will upload when you save`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    await uploadFilesToQuotation(id, files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Shared uploader used both by direct selection and by the auto-upload after save
  const uploadFilesToQuotation = async (quotationId, files) => {
    if (!quotationId || !files || files.length === 0) return null;
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    try {
      setUploadingAttachments(true);
      setUploadProgress(0);
      const response = await quotationAPI.uploadAttachments(quotationId, formData, (evt) => {
        if (evt.total) setUploadProgress(Math.round((evt.loaded * 100) / evt.total));
      });
      setAttachments(response.data?.attachments || []);
      toast.success(`${files.length} file${files.length > 1 ? 's' : ''} uploaded`);
      return response.data?.attachments || [];
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to upload file');
      return null;
    } finally {
      setUploadingAttachments(false);
      setUploadProgress(0);
    }
  };

  const handleRemoveAttachment = async (attachmentId) => {
    if (!id || !attachmentId) return;
    try {
      const response = await quotationAPI.deleteAttachment(id, attachmentId);
      setAttachments(response.data?.attachments || []);
      toast.success('Attachment removed');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to remove attachment');
    }
  };

  const handleRemovePendingFile = (localId) => {
    setPendingFiles((prev) => prev.filter((p) => p.localId !== localId));
  };

  const handleAttachmentDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleAttachmentFiles(e.dataTransfer?.files);
  };

  const handleDownloadPDF = async () => {
    if (!requireCustomer('PDF quote')) return;
    setPdfLoading(true);
    try {
      let response;
      if (id) {
        // Download saved quotation PDF
        response = await quotationAPI.downloadPDF(id);
      } else {
        // Generate preview PDF without saving
        const totalsData = calculateTotals();
        const previewData = {
          ...formData,
          customer: formData.customer?._id || formData.customer,
          customerSnapshot: formData.customer ? {
            name: formData.customer.name,
            email: formData.customer.email,
            phone: formData.customer.phone || formData.customer.mobile,
            company: formData.customer.company,
            address: formData.customer.address,
            gstNumber: formData.customer.gstNumber || formData.customer.gstin || '',
            gstin: formData.customer.gstin || formData.customer.gstNumber || '',
            panNumber: formData.customer.panNumber || formData.customer.pan || '',
          } : {},
          subtotal: totalsData.subtotal,
          totalDiscount: totalsData.totalDiscount,
          totalTax: totalsData.totalTax,
          grandTotal: totalsData.grandTotal
        };
        response = await quotationAPI.previewPDF(previewData);
      }
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const quotationNum = response.headers['x-quotation-number'] || formData.quotationNumber;
      link.download = `Quotation - ${quotationNum}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePrint = async () => {
    if (!requireCustomer('print')) return;
    // Auto-save first so we always print a persisted, numbered quotation
    const saved = await handleSubmit(null, { skipNavigation: true });
    if (!saved || !saved._id) return; // save failed (toast already shown) or no id returned

    try {
      const response = await quotationAPI.downloadPDF(saved._id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const quotationNum = response.headers['x-quotation-number'] || saved.quotationNumber || formData.quotationNumber;
      link.download = `Quotation - ${quotationNum}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    }

    // For a newly-created quotation, reflect its real id in the URL so further edits
    // target the saved record instead of creating duplicates on the next save.
    if (!isEdit) navigate(`/quotations/${saved._id}/edit`, { replace: true });
  };

  const handleConfirm = () => {
    if (!requireCustomer('confirm')) return;
    setStatus('accepted');
    toast.success('Quotation confirmed');
  };

  const handlePreview = () => {
    if (!requireCustomer('preview')) return;
    if (id) {
      navigate(`/quotations/${id}`);
    } else {
      toast.info('Please save the quotation first to preview');
    }
  };

  const formatCurrency = (amount) => {
    return `₹ ${(amount || 0).toFixed(2)}`;
  };

  // Handler for adding custom payment term
  const handleAddCustomPaymentTerm = () => {
    if (!newPaymentTerm.name.trim()) {
      toast.error('Please enter a payment term name');
      return;
    }
    const termId = `custom_${Date.now()}`;
    const newTerm = {
      id: termId,
      name: newPaymentTerm.name,
      days: newPaymentTerm.days ? parseInt(newPaymentTerm.days) : null,
      description: newPaymentTerm.description,
      isCustom: true,
    };
    const updatedCustomTerms = [...customPaymentTerms, newTerm];
    setCustomPaymentTerms(updatedCustomTerms);
    setFormData({ ...formData, paymentTerms: termId });
    setNewPaymentTerm({ name: '', days: '', description: '' });
    setPaymentTermDialogOpen(false);
    setPaymentTermSearchText('');
    // Navigate to last page to show the new term
    const totalTerms = predefinedPaymentTerms.length + updatedCustomTerms.length;
    const lastPage = Math.max(0, Math.ceil(totalTerms / paymentTermsPerPage) - 1);
    setPaymentTermPage(lastPage);
    // Open search dialog to show the new term
    setPaymentTermSearchDialogOpen(true);
    toast.success('Custom payment term added');
  };

  // Handler for adding custom product
  const handleAddCustomProduct = async () => {
    if (!newProduct.name.trim()) {
      toast.error('Please enter a product name');
      return;
    }

    let createdProduct;
    try {
      const response = await productAPI.create({
        name: newProduct.name,
        productType: newProduct.productType,
        unitPrice: parseFloat(newProduct.salesPrice) || 0,
        taxRate: parseFloat(newProduct.salesTax) || 18,
        category: newProduct.category || 'other',
        sku: newProduct.reference || undefined,
        hsnCode: newProduct.hsnCode || '',
        description: newProduct.internalNotes || '',
        canBeSold: newProduct.canBeSold,
        canBePurchased: newProduct.canBePurchased,
        trackInventory: newProduct.trackInventory,
      });
      createdProduct = response.data.product || response.data;
    } catch (error) {
      console.error('Error creating product:', error);
      toast.error(error.response?.data?.message || 'Failed to create product');
      return;
    }

    setProducts([...products, createdProduct]);

    // If triggered from a specific row, set the product there
    if (productDialogIndex !== null) {
      const newItems = [...formData.items];
      newItems[productDialogIndex] = {
        ...newItems[productDialogIndex],
        product: createdProduct._id,
        productName: createdProduct.name,
        description: createdProduct.description || '',
        unitPrice: createdProduct.unitPrice || 0,
        taxRate: createdProduct.taxRate !== undefined && createdProduct.taxRate !== null
          ? createdProduct.taxRate
          : 18,
      };
      setFormData({ ...formData, items: newItems });
    }

    setNewProduct({
      name: '',
      productType: 'goods',
      canBeSold: true,
      canBePurchased: true,
      isRental: false,
      trackInventory: false,
      invoicingPolicy: 'ordered',
      salesPrice: '',
      salesTax: '18',
      cost: '',
      purchaseTax: '18',
      category: '',
      reference: '',
      barcode: '',
      hsnCode: '',
      company: 'all',
      internalNotes: '',
    });
    setProductDialogOpen(false);
    setProductDialogIndex(null);
    setProductTab(0);
    toast.success('Custom product added');
  };

  // Handler for adding custom customer
  const handleAddCustomCustomer = async () => {
    if (!newCustomer.name.trim()) {
      toast.error('Please enter a customer name');
      return;
    }
    
    try {
      const response = await customerAPI.create({
        name: newCustomer.name,
        email: newCustomer.email,
        phone: newCustomer.phone || '',
        company: newCustomer.company || '',
        customerType: newCustomer.type === 'company' ? 'business' : 'individual',
        gstNumber: newCustomer.gstin || '',
        address: {
          street: newCustomer.street || '',
          city: newCustomer.city || '',
          state: newCustomer.state || '',
          zipCode: newCustomer.zip || '',
          country: newCustomer.country || 'India',
        },
      });
      
      const createdCustomer = response.data.customer;
      setCustomers([...customers, createdCustomer]);
      setFormData({ ...formData, customer: createdCustomer });
      setCustomerError(false);
      
      setNewCustomer({
        type: 'person',
        name: '',
        email: '',
        phone: '',
        company: '',
        street: '',
        city: '',
        zip: '',
        state: '',
        country: 'India',
        gstin: '',
      });
      setCustomerTab(0);
      setCustomerDialogOpen(false);
      toast.success('Customer created successfully');
    } catch (error) {
      console.error('Error creating customer:', error);
      toast.error(error.response?.data?.message || 'Failed to create customer');
    }
  };

  // Handler for payment terms change
  const handlePaymentTermsChange = (e) => {
    const value = e.target.value;
    if (value === 'search_more') {
      setPaymentTermSearchDialogOpen(true);
    } else {
      setFormData({ ...formData, paymentTerms: value });
    }
  };

  // Select payment term from search dialog
  const handleSelectPaymentTerm = (termId) => {
    setFormData({ ...formData, paymentTerms: termId });
    setPaymentTermSearchDialogOpen(false);
    setPaymentTermSearchText('');
    setPaymentTermPage(0);
  };

  // Get display name for selected payment term
  const getPaymentTermDisplayName = () => {
    const term = allPaymentTerms.find(t => t.id === formData.paymentTerms);
    return term ? term.name : formData.paymentTerms;
  };

  // Filter payment terms by search
  const filteredPaymentTerms = allPaymentTerms.filter(term =>
    term.name.toLowerCase().includes(paymentTermSearchText.toLowerCase())
  );

  // Paginated payment terms
  const totalPaymentTermPages = Math.ceil(filteredPaymentTerms.length / paymentTermsPerPage);
  const paginatedPaymentTerms = filteredPaymentTerms.slice(
    paymentTermPage * paymentTermsPerPage,
    (paymentTermPage + 1) * paymentTermsPerPage
  );
  const paymentTermStartIndex = paymentTermPage * paymentTermsPerPage + 1;
  const paymentTermEndIndex = Math.min((paymentTermPage + 1) * paymentTermsPerPage, filteredPaymentTerms.length);

  // Open product dialog from a specific row
  const openProductDialog = (index) => {
    setProductDialogIndex(index);
    setProductDialogOpen(true);
    setOpenDropdownIndex(null);
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ backgroundColor: '#f0f0f0', minHeight: '100%', overflow: 'hidden' }}>
        {/* Sub Header - Breadcrumb */}
        <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #dee2e6', px: { xs: 1, sm: 2 }, py: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate('/quotations/new')}
              sx={{
                backgroundColor: '#875A7B',
                color: 'white',
                textTransform: 'none',
                minWidth: 'auto',
                px: 1.5,
                py: 0.25,
                fontSize: '13px',
                boxShadow: 'none',
                '&:hover': { backgroundColor: '#714B68', boxShadow: 'none' },
              }}
            >
              New
            </Button>
            <Typography
              variant="body2"
              sx={{ color: '#6c757d', cursor: 'pointer', fontSize: '13px' }}
              onClick={() => navigate('/quotations')}
            >
              Quotations
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
            <Typography variant="body2" sx={{ color: '#212529', fontWeight: 400, fontSize: '13px' }}>
              {isEdit ? 'Edit' : 'New'}
            </Typography>
            <Box
              sx={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                '&:hover .custom-tooltip': {
                  visibility: 'visible',
                  opacity: 1,
                },
              }}
            >
              <Settings 
                onClick={(e) => setSettingsMenuAnchor(e.currentTarget)}
                sx={{ fontSize: 14, color: '#6c757d', cursor: 'pointer' }} 
              />
              <Box
                className="custom-tooltip"
                sx={{
                  visibility: 'hidden',
                  opacity: 0,
                  width: 'max-content',
                  backgroundColor: '#333',
                  color: '#fff',
                  textAlign: 'center',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  position: 'absolute',
                  zIndex: 1,
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  marginTop: '4px',
                  fontSize: '11px',
                  transition: 'opacity 0.3s ease-in-out',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    marginLeft: '-4px',
                    borderWidth: '4px',
                    borderStyle: 'solid',
                    borderColor: 'transparent transparent #333 transparent',
                  },
                }}
              >
                Actions
              </Box>
            </Box>
            {/* Settings Menu */}
            <MuiMenu
              anchorEl={settingsMenuAnchor}
              open={Boolean(settingsMenuAnchor)}
              onClose={() => {
                setSettingsMenuAnchor(null);
                setPrintSubmenuAnchor(null);
              }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{ sx: { minWidth: 200, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' } }}
            >
              <MenuItem 
                onClick={(e) => {
                  e.stopPropagation();
                  setPrintSubmenuAnchor(printSubmenuAnchor ? null : e.currentTarget);
                }}
                sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', py: 1, backgroundColor: printSubmenuAnchor ? 'rgba(0,0,0,0.04)' : 'transparent' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Print sx={{ fontSize: 18, color: '#666' }} />
                  Print
                </Box>
                <KeyboardArrowRight sx={{ fontSize: 16, color: '#666' }} />
              </MenuItem>
              <MenuItem 
                onClick={() => {
                  setSettingsMenuAnchor(null);
                  setPrintSubmenuAnchor(null);
                  toast.info('Duplicate functionality coming soon');
                }}
                sx={{ fontSize: '13px', py: 1 }}
              >
                <FileCopy sx={{ fontSize: 18, color: '#666', mr: 1 }} />
                Duplicate
              </MenuItem>
              <Divider sx={{ my: 0.5 }} />
              <MenuItem 
                onClick={() => {
                  setSettingsMenuAnchor(null);
                  setPrintSubmenuAnchor(null);
                  if (!requireCustomer('generate payment link')) return;
                  toast.info('Generate Payment Link coming soon');
                }}
                sx={{ fontSize: '13px', py: 1 }}
              >
                <LinkIcon sx={{ fontSize: 18, color: '#666', mr: 1 }} />
                Generate a Payment Link
              </MenuItem>
              <MenuItem 
                onClick={() => {
                  setSettingsMenuAnchor(null);
                  setPrintSubmenuAnchor(null);
                  if (!requireCustomer('send email')) return;
                  toast.info('Send an email coming soon');
                }}
                sx={{ fontSize: '13px', py: 1 }}
              >
                <Email sx={{ fontSize: 18, color: '#666', mr: 1 }} />
                Send an email
              </MenuItem>
              <MenuItem 
                onClick={() => {
                  setSettingsMenuAnchor(null);
                  setPrintSubmenuAnchor(null);
                  if (!requireCustomer('mark as sent')) return;
                  setStatus('sent');
                  toast.success('Quotation marked as sent');
                }}
                sx={{ fontSize: '13px', py: 1 }}
              >
                <MarkEmailRead sx={{ fontSize: 18, color: '#666', mr: 1 }} />
                Mark Quotation as Sent
              </MenuItem>
              <MenuItem 
                onClick={() => {
                  setSettingsMenuAnchor(null);
                  setPrintSubmenuAnchor(null);
                  if (!requireCustomer('share')) return;
                  toast.info('Share functionality coming soon');
                }}
                sx={{ fontSize: '13px', py: 1 }}
              >
                <Share sx={{ fontSize: 18, color: '#666', mr: 1 }} />
                Share
              </MenuItem>
              <MenuItem 
                onClick={() => {
                  setSettingsMenuAnchor(null);
                  setPrintSubmenuAnchor(null);
                  if (!requireCustomer('create project')) return;
                  toast.info('Create Project coming soon');
                }}
                sx={{ fontSize: '13px', py: 1 }}
              >
                <FolderOpen sx={{ fontSize: 18, color: '#666', mr: 1 }} />
                Create Project
              </MenuItem>
            </MuiMenu>
            {/* Print Submenu */}
            <MuiMenu
              anchorEl={printSubmenuAnchor}
              open={Boolean(printSubmenuAnchor)}
              onClose={() => setPrintSubmenuAnchor(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{ sx: { minWidth: 200, boxShadow: '0 2px 8px rgba(0,0,0,0.15)', ml: 0.5 } }}
            >
              <MenuItem 
                onClick={() => {
                  setSettingsMenuAnchor(null);
                  setPrintSubmenuAnchor(null);
                  handleDownloadPDF();
                }}
                disabled={pdfLoading}
                sx={{ fontSize: '13px', py: 1 }}
              >
                {pdfLoading
                  ? <CircularProgress size={18} sx={{ color: '#666', mr: 1 }} />
                  : <PictureAsPdf sx={{ fontSize: 18, color: '#666', mr: 1 }} />}
                {pdfLoading ? 'Generating PDF...' : 'PDF Quote'}
              </MenuItem>
              <MenuItem 
                onClick={() => {
                  setSettingsMenuAnchor(null);
                  setPrintSubmenuAnchor(null);
                  handleDownloadPDF();
                }}
                disabled={pdfLoading}
                sx={{ fontSize: '13px', py: 1 }}
              >
                {pdfLoading
                  ? <CircularProgress size={18} sx={{ color: '#666', mr: 1 }} />
                  : <Description sx={{ fontSize: 18, color: '#666', mr: 1 }} />}
                {pdfLoading ? 'Generating PDF...' : 'Quotation / Order'}
              </MenuItem>
              <MenuItem 
                onClick={() => {
                  setSettingsMenuAnchor(null);
                  setPrintSubmenuAnchor(null);
                  if (!requireCustomer('pickup receipt')) return;
                  toast.info('Pickup and Return Receipt coming soon');
                }}
                sx={{ fontSize: '13px', py: 1 }}
              >
                <LocalShipping sx={{ fontSize: 18, color: '#666', mr: 1 }} />
                Pickup and Return Receipt
              </MenuItem>
            </MuiMenu>
            <Box
              sx={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                '&:hover .custom-tooltip': {
                  visibility: 'visible',
                  opacity: 1,
                },
              }}
            >
              <CloudUpload 
                onClick={handleSubmit}
                sx={{ fontSize: 14, color: saving ? '#ccc' : '#6c757d', cursor: saving ? 'not-allowed' : 'pointer' }} 
              />
              {saving && <CircularProgress size={12} sx={{ position: 'absolute', top: 1, left: 1 }} />}
              <Box
                className="custom-tooltip"
                sx={{
                  visibility: 'hidden',
                  opacity: 0,
                  width: 'max-content',
                  backgroundColor: '#333',
                  color: '#fff',
                  textAlign: 'center',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  position: 'absolute',
                  zIndex: 1,
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  marginTop: '4px',
                  fontSize: '11px',
                  transition: 'opacity 0.3s ease-in-out',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    marginLeft: '-4px',
                    borderWidth: '4px',
                    borderStyle: 'solid',
                    borderColor: 'transparent transparent #333 transparent',
                  },
                }}
              >
                Save manually
              </Box>
            </Box>
            <Box
              sx={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                '&:hover .custom-tooltip': {
                  visibility: 'visible',
                  opacity: 1,
                },
              }}
            >
              <Close
                sx={{ fontSize: 14, color: '#6c757d', cursor: 'pointer' }}
                onClick={() => navigate('/quotations')}
              />
              <Box
                className="custom-tooltip"
                sx={{
                  visibility: 'hidden',
                  opacity: 0,
                  width: 'max-content',
                  backgroundColor: '#333',
                  color: '#fff',
                  textAlign: 'center',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  position: 'absolute',
                  zIndex: 1,
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  marginTop: '4px',
                  fontSize: '11px',
                  transition: 'opacity 0.3s ease-in-out',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    marginLeft: '-4px',
                    borderWidth: '4px',
                    borderStyle: 'solid',
                    borderColor: 'transparent transparent #333 transparent',
                  },
                }}
              >
                Discard all changes
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Action Bar */}
        <Box
          sx={{
            backgroundColor: 'white',
            borderBottom: '1px solid #dee2e6',
            px: { xs: 1, sm: 2 },
            py: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {/* Mobile: Show 3-dot menu */}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <IconButton
                size="small"
                onClick={(e) => setActionMenuAnchor(e.currentTarget)}
                sx={{ 
                  border: '1px solid #dee2e6',
                  borderRadius: 1,
                  height: 30,
                  width: 30,
                }}
              >
                <MoreVert sx={{ fontSize: 20 }} />
              </IconButton>
              <MuiMenu
                anchorEl={actionMenuAnchor}
                open={Boolean(actionMenuAnchor)}
                onClose={() => setActionMenuAnchor(null)}
                PaperProps={{ sx: { minWidth: 150, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' } }}
              >
                <MenuItem onClick={() => { setActionMenuAnchor(null); handlePrint(); }}>
                  <Print sx={{ fontSize: 18, color: '#666', mr: 1 }} />
                  Print
                </MenuItem>
                <MenuItem onClick={() => { setActionMenuAnchor(null); handleConfirm(); }}>
                  <CheckCircle sx={{ fontSize: 18, color: '#666', mr: 1 }} />
                  Confirm
                </MenuItem>
                <MenuItem onClick={() => { setActionMenuAnchor(null); handlePreview(); }}>
                  <Visibility sx={{ fontSize: 18, color: '#666', mr: 1 }} />
                  Preview
                </MenuItem>
              </MuiMenu>
            </Box>
            {/* Desktop: Show individual buttons */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Print sx={{ fontSize: 16 }} />}
                onClick={handlePrint}
                sx={{ textTransform: 'none', borderColor: '#dee2e6', color: '#333', fontWeight: 400, fontSize: '13px', height: 30, px: 1.5 }}
              >
                Print
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<CheckCircle sx={{ fontSize: 16 }} />}
                onClick={handleConfirm}
                sx={{ textTransform: 'none', borderColor: '#dee2e6', color: '#333', fontWeight: 400, fontSize: '13px', height: 30, px: 1.5 }}
              >
                Confirm
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Visibility sx={{ fontSize: 16 }} />}
                onClick={handlePreview}
                sx={{ textTransform: 'none', borderColor: '#dee2e6', color: '#333', fontWeight: 400, fontSize: '13px', height: 30, px: 1.5 }}
              >
                Preview
              </Button>
            </Box>
          </Box>

          {/* Right side - Status Progress Indicator (Arrow Steps - Odoo Style) */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', height: 30 }}>
            {/* Quotation Step */}
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                px: 2,
                backgroundColor: 'white',
                color: status === 'draft' ? '#017e84' : '#888',
                fontSize: '13px',
                fontWeight: status === 'draft' ? 500 : 400,
                border: status === 'draft' ? '1px solid #017e84' : '1px solid #d0d0d0',
                borderRight: 'none',
                borderTopLeftRadius: '3px',
                borderBottomLeftRadius: '3px',
                zIndex: 3,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  right: -12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 0,
                  height: 0,
                  borderTop: '15px solid transparent',
                  borderBottom: '15px solid transparent',
                  borderLeft: status === 'draft' ? '12px solid #017e84' : '12px solid #d0d0d0',
                  zIndex: 2,
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  right: -10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 0,
                  height: 0,
                  borderTop: '13px solid transparent',
                  borderBottom: '13px solid transparent',
                  borderLeft: '10px solid white',
                  zIndex: 3,
                },
              }}
            >
              Quotation
            </Box>
            {/* Quotation Sent Step */}
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                pl: 3,
                pr: 2,
                backgroundColor: 'white',
                color: status === 'sent' ? '#017e84' : '#888',
                fontSize: '13px',
                fontWeight: status === 'sent' ? 500 : 400,
                borderTop: status === 'sent' ? '1px solid #017e84' : '1px solid #d0d0d0',
                borderBottom: status === 'sent' ? '1px solid #017e84' : '1px solid #d0d0d0',
                zIndex: 2,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  right: -12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 0,
                  height: 0,
                  borderTop: '15px solid transparent',
                  borderBottom: '15px solid transparent',
                  borderLeft: status === 'sent' ? '12px solid #017e84' : '12px solid #d0d0d0',
                  zIndex: 2,
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  right: -10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 0,
                  height: 0,
                  borderTop: '13px solid transparent',
                  borderBottom: '13px solid transparent',
                  borderLeft: '10px solid white',
                  zIndex: 3,
                },
              }}
            >
              Quotation Sent
            </Box>
            {/* Sales Order Step */}
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                pl: 3,
                pr: 2,
                backgroundColor: 'white',
                color: status === 'converted' ? '#017e84' : '#888',
                fontSize: '13px',
                fontWeight: status === 'converted' ? 500 : 400,
                border: status === 'converted' ? '1px solid #017e84' : '1px solid #d0d0d0',
                borderLeft: 'none',
                borderTopRightRadius: '3px',
                borderBottomRightRadius: '3px',
                zIndex: 1,
              }}
            >
              Sales Order
            </Box>
          </Box>
        </Box>

        {/* Main Content */}
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
          {/* Main Form Panel - Full Width */}
          <Box sx={{ backgroundColor: 'white', borderRadius: 1, p: { xs: 2, sm: 3 }, mb: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 400, mb: { xs: 2, sm: 3 }, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
              New
            </Typography>

            {/* Form Fields */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                  <Typography sx={{ width: { xs: '100%', sm: 140 }, minWidth: { sm: 140 }, color: '#875A7B', fontSize: '14px', fontWeight: 600, mb: { xs: 0.5, sm: 0 }, pt: 1 }}>
                    Customer <span style={{ color: '#d32f2f' }}>*</span>
                  </Typography>
                  <Box sx={{ flex: 1, minWidth: { xs: 150, sm: 200 } }}>
                    <Autocomplete
                      options={customers}
                      getOptionLabel={(option) =>
                        option?.name ? `${option.name}${option.company ? ` (${option.company})` : ''}` : ''
                      }
                      isOptionEqualToValue={(option, value) => option?._id === value?._id}
                      autoHighlight
                      selectOnFocus
                      handleHomeEndKeys
                      openOnFocus
                      inputValue={customerInputValue}
                      onInputChange={(event, newInputValue) => {
                        setCustomerInputValue(newInputValue);
                      }}
                      filterOptions={(options, { inputValue }) => {
                        const q = (inputValue || '').trim().toLowerCase();
                        if (!q) return options;
                        return options.filter((option) => {
                          const name = (option?.name || '').toLowerCase();
                          const company = (option?.company || '').toLowerCase();
                          const email = (option?.email || '').toLowerCase();
                          const phone = (option?.phone || '').toLowerCase();
                          return (
                            name.includes(q) ||
                            company.includes(q) ||
                            email.includes(q) ||
                            phone.includes(q)
                          );
                        });
                      }}
                      value={formData.customer}
                      onChange={handleCustomerChange}
                      size="small"
                      noOptionsText={
                        <Typography sx={{ color: '#666', fontSize: '13px', textAlign: 'center', py: 1 }}>
                          No customers found
                        </Typography>
                      }
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 0,
                          '& fieldset': {
                            border: 'none',
                            borderBottom: customerError ? '2px solid #d32f2f' : '1px solid #e0e0e0',
                          },
                          '&:hover fieldset': {
                            border: 'none',
                            borderBottom: customerError ? '2px solid #d32f2f' : '1px solid #bdbdbd',
                          },
                          '&.Mui-focused fieldset': {
                            border: 'none',
                            borderBottom: customerError ? '2px solid #d32f2f' : '1px solid #017e84',
                          },
                        },
                      }}
                      PaperComponent={({ children, ...paperProps }) => (
                        <Box {...paperProps} sx={{ backgroundColor: 'white', boxShadow: 3, borderRadius: 1, overflow: 'hidden' }}>
                          {children}
                          <Divider />
                          <Box
                            sx={{ 
                              p: 1, 
                              textAlign: 'center',
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: '#f5f5f5' }
                            }}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            onClick={() => setCustomerDialogOpen(true)}
                          >
                            <Typography sx={{ color: '#017e84', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Add sx={{ fontSize: 16, mr: 0.5 }} /> Create New Customer
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Type to find a customer..."
                          size="small"
                          error={customerError}
                        />
                      )}
                    />
                    {customerError && (
                      <Typography sx={{ color: '#d32f2f', fontSize: '12px', mt: 0.5 }}>
                        Please select a customer first
                      </Typography>
                    )}
                    {formData.customer && (() => {
                      const addr = formData.customer.address || {};
                      const billing = formData.customer.billingAddress || {};
                      const pick = (k) => addr[k] || billing[k] || '';
                      const street = pick('street');
                      const street2 = addr.street2 || '';
                      const cityStateZip = [
                        [pick('city'), pick('state')].filter(Boolean).join(', '),
                        pick('zipCode'),
                      ].filter(Boolean).join(' ');
                      const country = pick('country');
                      if (!street && !street2 && !cityStateZip && !country) return null;
                      return (
                        <Box sx={{ mt: 1, display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
                          <LocationOn sx={{ fontSize: 16, mt: '2px', color: '#875A7B' }} />
                          <Box sx={{ fontSize: '12px', color: '#666', lineHeight: 1.5 }}>
                            {street && <div>{street}</div>}
                            {street2 && <div>{street2}</div>}
                            {cityStateZip && <div>{cityStateZip}</div>}
                            {country && <div>{country}</div>}
                          </Box>
                        </Box>
                      );
                    })()}
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                  <Typography sx={{ width: { xs: '100%', sm: 130 }, minWidth: { sm: 130 }, color: '#333', fontSize: '14px', fontWeight: 600, mb: { xs: 0.5, sm: 0 } }}>
                    Expiration <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                  </Typography>
                  <DatePicker
                    value={formData.validUntil}
                    onChange={(newValue) => setFormData({ ...formData, validUntil: newValue })}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { 
                          minWidth: 150,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 0,
                            '& fieldset': {
                              border: 'none',
                              borderBottom: '1px solid #e0e0e0',
                            },
                            '&:hover fieldset': {
                              border: 'none',
                              borderBottom: '1px solid #bdbdbd',
                            },
                            '&.Mui-focused fieldset': {
                              border: 'none',
                              borderBottom: '1px solid #017e84',
                            },
                          },
                        },
                      },
                    }}
                    format="DD/MM/YYYY"
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                  <Typography sx={{ width: { xs: '100%', sm: 130 }, minWidth: { sm: 130 }, color: '#333', fontSize: '14px', fontWeight: 600, mb: { xs: 0.5, sm: 0 } }}>
                    Pricelist <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                  </Typography>
                  <TextField
                    select
                    size="small"
                    value={formData.pricelist}
                    onChange={(e) => setFormData({ ...formData, pricelist: e.target.value })}
                    sx={{ 
                      minWidth: 150,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 0,
                        '& fieldset': {
                          border: 'none',
                          borderBottom: '1px solid #e0e0e0',
                        },
                        '&:hover fieldset': {
                          border: 'none',
                          borderBottom: '1px solid #bdbdbd',
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none',
                          borderBottom: '1px solid #017e84',
                        },
                      },
                    }}
                  >
                    <MenuItem value="default_inr">Default (INR)</MenuItem>
                    <MenuItem value="search_more" sx={{ color: '#017e84', fontStyle: 'italic' }}>Search more...</MenuItem>
                  </TextField>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                  <Typography sx={{ width: { xs: '100%', sm: 130 }, minWidth: { sm: 130 }, color: '#333', fontSize: '14px', fontWeight: 600, mb: { xs: 0.5, sm: 0 } }}>Payment Terms</Typography>
                  <Box 
                    onClick={() => setPaymentTermSearchDialogOpen(true)}
                    sx={{ 
                      minWidth: 200,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      borderBottom: '1px solid #e0e0e0',
                      py: 0.75,
                      px: 0.5,
                      '&:hover': {
                        borderBottom: '1px solid #bdbdbd',
                      },
                    }}
                  >
                    <Typography sx={{ fontSize: '14px', color: '#333' }}>
                      {getPaymentTermDisplayName()}
                    </Typography>
                    <KeyboardArrowDown sx={{ fontSize: 20, color: '#666' }} />
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    minWidth: 'auto',
                    px: 2,
                    color: '#666',
                    fontSize: '13px',
                  },
                  '& .Mui-selected': {
                    color: '#212529 !important',
                    fontWeight: 500,
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#875A7B',
                  },
                }}
              >
                <Tab label="Order Lines" />
                <Tab label="Optional Products" />
                <Tab label="Other Info" />
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <AttachFile sx={{ fontSize: 16 }} />
                      Attachments
                      {attachments.length + pendingFiles.length > 0 && (
                        <Chip
                          label={attachments.length + pendingFiles.length}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: 11,
                            ml: 0.5,
                            backgroundColor: '#875A7B',
                            color: '#fff',
                          }}
                        />
                      )}
                    </Box>
                  }
                />
              </Tabs>
            </Box>

            {/* Order Lines Tab */}
            {activeTab === 0 && (
              <>
                <TableContainer sx={{ overflow: 'visible' }}>
                  <Table size="small" sx={{ minWidth: 650, tableLayout: 'fixed' }}>
                    <TableHead>
                      <TableRow sx={{ '& th': { borderBottom: '2px solid #e0e0e0', py: 1, whiteSpace: 'nowrap' } }}>
                        <TableCell sx={{ width: 60, color: '#666', fontSize: '13px', fontWeight: 500 }}>SR. No</TableCell>
                        <TableCell sx={{ color: '#666', fontSize: '13px', fontWeight: 500, width: '40%' }}>Product</TableCell>
                        <TableCell sx={{ width: 80, color: '#666', fontSize: '13px', fontWeight: 500, textAlign: 'center' }}>Quantity</TableCell>
                        <TableCell sx={{ width: 80, color: '#666', fontSize: '13px', fontWeight: 500 }}>Unit Price</TableCell>
                        <TableCell sx={{ width: 80, color: '#666', fontSize: '13px', fontWeight: 500 }}>Taxes</TableCell>
                        <TableCell sx={{ width: 120, color: '#666', fontSize: '13px', fontWeight: 500, textAlign: 'right' }}>
                          Amount{' '}
                          <IconButton 
                            size="small" 
                            onClick={(e) => setColumnMenuAnchor(e.currentTarget)}
                            sx={{ p: 0.25 }}
                          >
                            <Tune sx={{ fontSize: 14, color: '#666' }} />
                          </IconButton>
                          <MuiMenu
                            anchorEl={columnMenuAnchor}
                            open={Boolean(columnMenuAnchor)}
                            onClose={() => setColumnMenuAnchor(null)}
                            PaperProps={{
                              sx: {
                                minWidth: 180,
                                py: 1,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                              },
                            }}
                          >
                            {[
                              { key: 'srNo', label: 'SR. No' },
                              { key: 'productVariant', label: 'Product Variant' },
                              { key: 'product', label: 'Product' },
                              { key: 'description', label: 'Description' },
                              { key: 'leadTime', label: 'Lead Time' },
                              { key: 'cost', label: 'Cost' },
                              { key: 'taxes', label: 'Taxes' },
                              { key: 'taxIncl', label: 'Tax Incl.' },
                            ].map((col) => (
                              <MenuItem 
                                key={col.key} 
                                onClick={() => setVisibleColumns(prev => ({ ...prev, [col.key]: !prev[col.key] }))}
                                sx={{ py: 0.5, px: 2 }}
                              >
                                <Checkbox 
                                  checked={visibleColumns[col.key]} 
                                  size="small"
                                  sx={{ 
                                    p: 0, 
                                    mr: 1,
                                    color: '#017e84',
                                    '&.Mui-checked': {
                                      color: '#017e84',
                                    },
                                  }} 
                                />
                                <Typography sx={{ fontSize: '13px', color: '#333' }}>{col.label}</Typography>
                              </MenuItem>
                            ))}
                          </MuiMenu>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.items.map((item, index) => (
                        <TableRow key={index} sx={{ '& td': { borderBottom: '1px solid #f0f0f0', py: 1, verticalAlign: 'top' } }}>
                          <TableCell sx={{ pt: 2 }}>{index + 1}</TableCell>
                          <TableCell colSpan={item.isSection || item.isNote ? 6 : 1}>
                            {item.isSection ? (
                              <EditableRowInput
                                value={item.sectionName}
                                onChange={(value) => {
                                  const newItems = [...formData.items];
                                  newItems[index].sectionName = value;
                                  setFormData({ ...formData, items: newItems });
                                }}
                                onDelete={() => removeLineItem(index)}
                                placeholder="Enter section name"
                                bold={true}
                              />
                            ) : item.isNote ? (
                              <EditableRowInput
                                value={item.noteText}
                                onChange={(value) => {
                                  const newItems = [...formData.items];
                                  newItems[index].noteText = value;
                                  setFormData({ ...formData, items: newItems });
                                }}
                                onDelete={() => removeLineItem(index)}
                                placeholder="Enter a description"
                              />
                            ) : (
                              <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Autocomplete
                                    size="small"
                                    options={[...products].sort((a, b) => {
                                      const catA = (a.category || 'other').toLowerCase();
                                      const catB = (b.category || 'other').toLowerCase();
                                      return catA.localeCompare(catB);
                                    })}
                                    value={products.find(p => p._id === item.product) || (item.product && item.productName ? { _id: item.product, name: item.productName } : null)}
                                    onChange={(e, newValue) => {
                                      handleItemChange(index, 'product', newValue ? newValue._id : '');
                                      // Close dropdown after selection
                                      setOpenDropdownIndex(null);
                                      setCategorySearchActive({});
                                      setCategorySearchText({});
                                    }}
                                    getOptionLabel={(option) => {
                                      // For display, show name with category if product has duplicate names
                                      const duplicateCount = products.filter(p => p.name === option.name).length;
                                      if (duplicateCount > 1) {
                                        const category = option.category ? `[${option.category}]` : '[No Category]';
                                        return `${option.name} ${category}`;
                                      }
                                      return option.name || '';
                                    }}
                                    groupBy={(option) => (option.category || 'other').toLowerCase()}
                                    open={openDropdownIndex === index}
                                    onOpen={() => {
                                      setOpenDropdownIndex(index);
                                      setProductSearchText('');
                                    }}
                                    onClose={(e, reason) => {
                                      // Always clear search state immediately when closing dropdown
                                      setProductSearchText('');
                                      setCategorySearchActive({});
                                      setCategorySearchText({});
                                      setOpenDropdownIndex(null);
                                      preventCloseRef.current = false;
                                    }}
                                    inputValue={openDropdownIndex === index ? productSearchText : (products.find(p => p._id === item.product)?.name || '')}
                                    onInputChange={(e, newValue, reason) => {
                                      if (reason === 'input') {
                                        setProductSearchText(newValue);
                                      } else if (reason === 'clear') {
                                        setProductSearchText('');
                                      }
                                    }}
                                    filterOptions={(options, { inputValue }) => {
                                      // Filter products based on search input
                                      if (!inputValue) return options;
                                      const searchLower = inputValue.toLowerCase();
                                      return options.filter(option =>
                                        option.name.toLowerCase().includes(searchLower) ||
                                        (option.category && option.category.toLowerCase().includes(searchLower))
                                      );
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        placeholder="Search a product"
                                        sx={{
                                          '& .MuiOutlinedInput-root': {
                                            borderRadius: 0,
                                            backgroundColor: 'transparent',
                                            '& fieldset': {
                                              border: 'none',
                                              borderBottom: itemErrors[index] ? '2px solid #d32f2f' : '1px solid #e0e0e0',
                                            },
                                            '&:hover fieldset': {
                                              border: 'none',
                                              borderBottom: itemErrors[index] ? '2px solid #d32f2f' : '1px solid #bdbdbd',
                                            },
                                            '&.Mui-focused fieldset': {
                                              border: 'none',
                                              borderBottom: itemErrors[index] ? '2px solid #d32f2f' : '1px solid #017e84',
                                            },
                                          },
                                        }}
                                      />
                                    )}
                                    renderGroup={(params) => {
                                      const categoryLabels = {
                                        hardware: 'Hardware',
                                        software: 'Software',
                                        service: 'Services',
                                        subscription: 'Subscriptions',
                                        consulting: 'Consulting',
                                        support: 'Support',
                                        training: 'Training',
                                        other: 'Other'
                                      };
                                      const isCollapsed = collapsedCategories[params.group];
                                      const isSearchActive = categorySearchActive[params.group];
                                      const searchText = categorySearchText[params.group] || '';
                                      
                                      // Filter children based on search text
                                      let filteredChildren = params.children;
                                      if (searchText && Array.isArray(params.children)) {
                                        filteredChildren = params.children.filter(child => {
                                          const childText = child?.props?.children || '';
                                          return typeof childText === 'string' && 
                                            childText.toLowerCase().includes(searchText.toLowerCase());
                                        });
                                      }
                                      
                                      return (
                                        <Box key={params.key}>
                                          {/* Category Header - Always visible */}
                                          <Box
                                            data-category-header="true"
                                            onMouseDown={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              preventCloseRef.current = true;
                                            }}
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'space-between',
                                              backgroundColor: '#f8f9fa',
                                              minHeight: '36px',
                                              px: 2,
                                              borderBottom: isSearchActive ? 'none' : '1px solid #e0e0e0',
                                            }}
                                          >
                                            {/* Expand/Collapse Arrow + Category Name */}
                                            <Box
                                              onMouseDown={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                preventCloseRef.current = true;
                                              }}
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setCollapsedCategories(prev => ({
                                                  ...prev,
                                                  [params.group]: !prev[params.group]
                                                }));
                                              }}
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                color: '#000',
                                                fontWeight: 600,
                                                fontSize: '13px',
                                                cursor: 'pointer',
                                                flex: 1,
                                                py: 1,
                                                '&:hover': { color: '#333' }
                                              }}
                                            >
                                              {isCollapsed ? (
                                                <ExpandMore sx={{ fontSize: 18, mr: 0.5, color: '#666' }} />
                                              ) : (
                                                <ExpandLess sx={{ fontSize: 18, mr: 0.5, color: '#666' }} />
                                              )}
                                              {categoryLabels[params.group] || params.group.charAt(0).toUpperCase() + params.group.slice(1)}
                                            </Box>
                                            
                                            {/* Search Icon */}
                                            <IconButton
                                              size="small"
                                              onMouseDown={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                preventCloseRef.current = true;
                                              }}
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (isSearchActive) {
                                                  setCategorySearchActive(prev => ({
                                                    ...prev,
                                                    [params.group]: false
                                                  }));
                                                  setCategorySearchText(prev => ({
                                                    ...prev,
                                                    [params.group]: ''
                                                  }));
                                                } else {
                                                  setCategorySearchActive(prev => ({
                                                    ...prev,
                                                    [params.group]: true
                                                  }));
                                                }
                                              }}
                                              sx={{ 
                                                p: 0.5,
                                                backgroundColor: isSearchActive ? 'rgba(74, 99, 181, 0.1)' : 'transparent',
                                                '&:hover': { backgroundColor: isSearchActive ? 'rgba(74, 99, 181, 0.2)' : 'rgba(0,0,0,0.04)' }
                                              }}
                                            >
                                              {isSearchActive ? (
                                                <Close sx={{ fontSize: 16, color: '#4A63B5' }} />
                                              ) : (
                                                <Search sx={{ fontSize: 16, color: '#666' }} />
                                              )}
                                            </IconButton>
                                          </Box>
                                          
                                          {/* Search Input Row - Only shown when search is active */}
                                          {isSearchActive && (
                                            <Box
                                              data-category-header="true"
                                              onMouseDown={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                preventCloseRef.current = true;
                                              }}
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                backgroundColor: '#fff',
                                                px: 2,
                                                py: 0.5,
                                                borderBottom: '1px solid #e0e0e0',
                                              }}
                                            >
                                              <Search sx={{ fontSize: 16, color: '#999', mr: 1 }} />
                                              <input
                                                type="text"
                                                placeholder={`Search in ${categoryLabels[params.group] || params.group}...`}
                                                value={searchText}
                                                onChange={(e) => {
                                                  setCategorySearchText(prev => ({
                                                    ...prev,
                                                    [params.group]: e.target.value
                                                  }));
                                                }}
                                                onMouseDown={(e) => {
                                                  e.stopPropagation();
                                                  preventCloseRef.current = true;
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                onKeyDown={(e) => e.stopPropagation()}
                                                onFocus={() => {
                                                  preventCloseRef.current = true;
                                                }}
                                                autoFocus
                                                style={{
                                                  flex: 1,
                                                  border: 'none',
                                                  outline: 'none',
                                                  backgroundColor: 'transparent',
                                                  fontSize: '13px',
                                                  color: '#333',
                                                  padding: '6px 0',
                                                }}
                                              />
                                            </Box>
                                          )}
                                          
                                          {/* Products List */}
                                          {!isCollapsed && filteredChildren}
                                        </Box>
                                      );
                                    }}
                                    renderOption={(props, option) => {
                                      // Show product name with category if there are duplicates
                                      const duplicateCount = products.filter(p => p.name === option.name).length;
                                      const displayLabel = duplicateCount > 1 
                                        ? `${option.name} [${option.category || 'Other'}]` 
                                        : option.name;
                                      return (
                                        <Box
                                          component="li"
                                          {...props}
                                          sx={{
                                            pl: 4,
                                            fontSize: '13px',
                                            '&:hover': { backgroundColor: '#e8f5f5' }
                                          }}
                                        >
                                          {displayLabel}
                                        </Box>
                                      );
                                    }}
                                    sx={{ flex: 1 }}
                                    isOptionEqualToValue={(option, value) => option._id === value?._id}
                                    noOptionsText={
                                      <Typography sx={{ color: '#666', fontSize: '13px', textAlign: 'center', py: 1 }}>
                                        No products found
                                      </Typography>
                                    }
                                    slotProps={{
                                      listbox: {
                                        sx: {
                                          // Hide scrollbar while keeping scroll functionality
                                          '&::-webkit-scrollbar': { display: 'none' },
                                          scrollbarWidth: 'none',
                                          msOverflowStyle: 'none',
                                        }
                                      },
                                      paper: {
                                        onMouseDown: (e) => {
                                          // Prevent dropdown from closing when clicking on category headers or create button
                                          if (e.target.closest('[data-category-header]') || e.target.closest('[data-create-product]')) {
                                            e.preventDefault();
                                            preventCloseRef.current = true;
                                          }
                                        },
                                        sx: { overflow: 'visible' },
                                        children: (defaultChildren) => (
                                          <>
                                            {defaultChildren}
                                            <Divider />
                                            <Box
                                              data-create-product
                                              sx={{ 
                                                p: 1, 
                                                textAlign: 'center',
                                                cursor: 'pointer',
                                                '&:hover': { backgroundColor: '#f5f5f5' }
                                              }}
                                              onClick={() => openProductDialog(index)}
                                            >
                                              <Typography sx={{ color: '#017e84', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <Add sx={{ fontSize: 16, mr: 0.5 }} /> Create New Product
                                              </Typography>
                                            </Box>
                                          </>
                                        )
                                      }
                                    }}
                                    PaperComponent={({ children, ...paperProps }) => (
                                      <Box {...paperProps} sx={{ backgroundColor: 'white', boxShadow: 3, borderRadius: 1, overflow: 'hidden' }}>
                                        {children}
                                        <Divider />
                                        <Box
                                          data-create-product
                                          sx={{ 
                                            p: 1, 
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            '&:hover': { backgroundColor: '#f5f5f5' }
                                          }}
                                          onMouseDown={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                          }}
                                          onClick={() => openProductDialog(index)}
                                        >
                                          <Typography sx={{ color: '#017e84', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Add sx={{ fontSize: 16, mr: 0.5 }} /> Create New Product
                                          </Typography>
                                        </Box>
                                      </Box>
                                    )}
                                  />
                                  <Box
                                    sx={{
                                      position: 'relative',
                                      display: 'inline-block',
                                      '&:hover .custom-tooltip': {
                                        visibility: 'visible',
                                        opacity: 1,
                                      },
                                    }}
                                  >
                                    <IconButton 
                                      size="small" 
                                      sx={{ p: 0.5 }}
                                      onClick={() => setExpandedDescriptions(prev => ({ ...prev, [index]: true }))}
                                    >
                                      <MenuIcon sx={{ fontSize: 20, color: '#666' }} />
                                    </IconButton>
                                    <Box
                                      className="custom-tooltip"
                                      sx={{
                                        visibility: 'hidden',
                                        opacity: 0,
                                        width: 'max-content',
                                        maxWidth: 220,
                                        backgroundColor: '#333',
                                        color: '#fff',
                                        textAlign: 'center',
                                        borderRadius: '6px',
                                        padding: '8px 12px',
                                        position: 'absolute',
                                        zIndex: 1,
                                        top: '100%',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        marginTop: '8px',
                                        fontSize: '12px',
                                        transition: 'opacity 0.3s ease-in-out',
                                        '&::after': {
                                          content: '""',
                                          position: 'absolute',
                                          bottom: '100%',
                                          left: '50%',
                                          marginLeft: '-5px',
                                          borderWidth: '5px',
                                          borderStyle: 'solid',
                                          borderColor: 'transparent transparent #333 transparent',
                                        },
                                      }}
                                    >
                                      Click or press enter to add a description
                                    </Box>
                                  </Box>
                                </Box>
                                {/* Description field - only show when icon is clicked */}
                                {(expandedDescriptions[index] || item.description) && (
                                  <TextField
                                    fullWidth
                                    size="small"
                                    value={item.description || ''}
                                    onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                                    placeholder="Enter a description"
                                    sx={{
                                      mt: 0.5,
                                      '& .MuiInputBase-root': {
                                        fontSize: '13px',
                                        fontStyle: 'italic',
                                        backgroundColor: 'transparent',
                                      },
                                      '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                          border: 'none',
                                        },
                                      },
                                      '& .MuiInputBase-input::placeholder': {
                                        fontStyle: 'italic',
                                        color: '#999',
                                        opacity: 1,
                                      },
                                    }}
                                    onBlur={() => {
                                      if (!item.description) {
                                        setExpandedDescriptions(prev => ({ ...prev, [index]: false }));
                                      }
                                    }}
                                    autoFocus={expandedDescriptions[index] && !item.description}
                                  />
                                )}
                              </Box>
                            )}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', pt: 2 }}>
                            {!item.isSection && !item.isNote && (
                              <TextField
                                type="number"
                                size="small"
                                value={item.quantity}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  handleItemChange(index, 'quantity', val === '' ? '' : parseFloat(val));
                                }}
                                onBlur={(e) => {
                                  const val = parseFloat(e.target.value);
                                  if (!val || val <= 0) {
                                    handleItemChange(index, 'quantity', 1);
                                  }
                                }}
                                inputProps={{ min: 1, step: 0.01 }}
                                sx={{
                                  width: 70,
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 0,
                                    '& fieldset': {
                                      border: 'none',
                                      borderBottom: '1px solid transparent',
                                    },
                                    '&:hover fieldset': {
                                      borderBottom: '1px solid #e0e0e0',
                                    },
                                    '&.Mui-focused fieldset': {
                                      border: 'none',
                                      borderBottom: '1px solid #017e84',
                                    },
                                  },
                                  '& .MuiInputBase-input': {
                                    textAlign: 'center',
                                    fontSize: '13px',
                                    py: 0.5,
                                    px: 0,
                                  },
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell sx={{ pt: 2 }}>
                            {!item.isSection && !item.isNote && (
                              <TextField
                                type="number"
                                size="small"
                                value={item.unitPrice}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  handleItemChange(index, 'unitPrice', val === '' ? '' : parseFloat(val));
                                }}
                                onBlur={(e) => {
                                  const val = parseFloat(e.target.value);
                                  if (isNaN(val)) {
                                    handleItemChange(index, 'unitPrice', 0);
                                  }
                                }}
                                inputProps={{ min: 0, step: 1 }}
                                sx={{
                                  width: 90,
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 0,
                                    '& fieldset': {
                                      border: 'none',
                                      borderBottom: '1px solid transparent',
                                    },
                                    '&:hover fieldset': {
                                      borderBottom: '1px solid #e0e0e0',
                                    },
                                    '&.Mui-focused fieldset': {
                                      border: 'none',
                                      borderBottom: '1px solid #017e84',
                                    },
                                  },
                                  '& .MuiInputBase-input': {
                                    textAlign: 'right',
                                    fontSize: '13px',
                                    py: 0.5,
                                    px: 0,
                                  },
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell sx={{ pt: 2 }}>
                            {!item.isSection && !item.isNote && (
                              <TextField
                                type="number"
                                size="small"
                                value={item.taxRate}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  handleItemChange(index, 'taxRate', val === '' ? '' : parseFloat(val));
                                }}
                                onBlur={(e) => {
                                  const val = parseFloat(e.target.value);
                                  if (isNaN(val) || val === 0) {
                                    handleItemChange(index, 'taxRate', 18);
                                  }
                                }}
                                inputProps={{ min: 0, max: 100, step: 1 }}
                                InputProps={{
                                  endAdornment: <InputAdornment position="end" sx={{ ml: -0.5, '& .MuiTypography-root': { fontSize: '13px', color: '#666' } }}>%</InputAdornment>,
                                }}
                                sx={{
                                  width: 70,
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 0,
                                    pr: 0.5,
                                    '& fieldset': {
                                      border: 'none',
                                      borderBottom: '1px solid transparent',
                                    },
                                    '&:hover fieldset': {
                                      borderBottom: '1px solid #e0e0e0',
                                    },
                                    '&.Mui-focused fieldset': {
                                      border: 'none',
                                      borderBottom: '1px solid #017e84',
                                    },
                                  },
                                  '& .MuiInputBase-input': {
                                    textAlign: 'right',
                                    fontSize: '13px',
                                    py: 0.5,
                                    px: 0,
                                    pr: 0.25,
                                  },
                                  '& .MuiInputAdornment-root': {
                                    ml: 0,
                                  },
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right', pt: 2 }}>
                            {!item.isSection && !item.isNote && (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                <Typography sx={{ fontSize: '13px', color: '#333' }}>
                                  ₹ {parseFloat(calculateLineTotal(item) || 0).toFixed(2)}
                                </Typography>
                                <IconButton size="small" onClick={() => removeLineItem(index)} sx={{ ml: 0.5 }}>
                                  <Delete sx={{ fontSize: 16, color: '#9e9e9e' }} />
                                </IconButton>
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Add Actions */}
                <Box sx={{ display: 'flex', gap: { xs: 2, sm: 3 }, mt: 2, mb: 3, flexWrap: 'wrap' }}>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={addLineItem}
                    sx={{ color: '#017e84', textDecoration: 'none', cursor: 'pointer', fontSize: '13px' }}
                  >
                    Add a product
                  </Link>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => {
                      setProductDialogIndex(null);
                      setProductDialogOpen(true);
                    }}
                    sx={{ color: '#017e84', textDecoration: 'none', cursor: 'pointer', fontSize: '13px' }}
                  >
                    Create new product
                  </Link>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={addSection}
                    sx={{ color: '#017e84', textDecoration: 'none', cursor: 'pointer', fontSize: '13px' }}
                  >
                    Add a section
                  </Link>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={addNote}
                    sx={{ color: '#017e84', textDecoration: 'none', cursor: 'pointer', fontSize: '13px' }}
                  >
                    Add a note
                  </Link>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={handleCatalogClick}
                    sx={{ color: '#017e84', textDecoration: 'none', cursor: 'pointer', fontSize: '13px' }}
                  >
                    Catalog
                  </Link>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Bottom Section */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', gap: 3 }}>
                  {/* Company Info / Editable Terms & Conditions */}
                  <Box sx={{ flex: 1, maxWidth: { md: 450 } }}>
                    <Typography variant="body2" sx={{ fontSize: '12px', color: '#333', mb: 0.5 }}>
                      <strong>CO</strong>mpany Name: Tech 4 Logic Pvt Ltd.
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '12px', color: '#333', mb: 0.5 }}>
                      Bank Name: HDFC Bank Ltd.
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '12px', color: '#333', mb: 0.5 }}>
                      A/c No: 5020001072015
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '12px', color: '#333', mb: 0.5 }}>
                      IFSC Code: HDFC0001382
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '12px', color: '#333', mb: 2 }}>
                      Branch Name PIONEER SQUARE SECTOR - 62, GURGAON
                    </Typography>

                    <Box
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        p: 1.5,
                        backgroundColor: '#fff',
                        transition: 'border-color 0.15s ease-in-out',
                        '&:hover': { borderColor: '#bdbdbd' },
                        '&:focus-within': { borderColor: '#017e84' },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontSize: '12px', fontWeight: 600, color: '#017e84', mb: 1 }}
                      >
                        Terms & Conditions
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        minRows={8}
                        variant="standard"
                        value={formData.termsAndConditions}
                        onChange={(e) => setFormData({ ...formData, termsAndConditions: e.target.value })}
                        placeholder="Enter terms and conditions..."
                        InputProps={{
                          disableUnderline: true,
                          sx: {
                            fontSize: '11px',
                            lineHeight: 1.7,
                            color: '#333',
                            fontFamily: 'inherit',
                            alignItems: 'flex-start',
                            '& textarea': { p: 0 },
                          },
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Totals */}
                  <Box sx={{ width: { xs: '100%', sm: 280 }, textAlign: 'right', alignSelf: { xs: 'flex-end', md: 'flex-start' } }}>
                    {/* Tax Type Selector */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                      <TextField
                        select
                        size="small"
                        value={formData.taxType}
                        onChange={(e) => setFormData({ ...formData, taxType: e.target.value })}
                        sx={{ 
                          width: 180,
                          '& .MuiOutlinedInput-root': {
                            fontSize: '13px',
                          }
                        }}
                        label="Tax Type"
                      >
                        <MenuItem value="igst">IGST (Inter-State)</MenuItem>
                        <MenuItem value="gst">GST (Intra-State)</MenuItem>
                      </TextField>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography sx={{ color: '#666', fontSize: '13px' }}>Untaxed Amount:</Typography>
                      <Typography sx={{ fontSize: '13px' }}>{formatCurrency(totals.subtotal)}</Typography>
                    </Box>
                    
                    {/* Tax Display based on selection */}
                    {formData.taxType === 'gst' ? (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography sx={{ color: '#666', fontSize: '13px' }}>CGST:</Typography>
                          <Typography sx={{ fontSize: '13px' }}>{formatCurrency(totals.cgst)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography sx={{ color: '#666', fontSize: '13px' }}>SGST:</Typography>
                          <Typography sx={{ fontSize: '13px' }}>{formatCurrency(totals.sgst)}</Typography>
                        </Box>
                      </>
                    ) : (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography sx={{ color: '#666', fontSize: '13px' }}>IGST:</Typography>
                        <Typography sx={{ fontSize: '13px' }}>{formatCurrency(totals.igst)}</Typography>
                      </Box>
                    )}
                    
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography sx={{ color: '#333', fontSize: '14px', fontWeight: 600 }}>Total:</Typography>
                      <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                        {formatCurrency(totals.grandTotal)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </>
            )}

            {/* Optional Products Tab */}
            {activeTab === 1 && (
              <Box sx={{ p: 2 }}>
                <Typography color="text.secondary">
                  Add optional products that customers can choose to add to their order.
                </Typography>
                <Button
                  startIcon={<Add />}
                  sx={{ mt: 2, color: '#875A7B' }}
                  onClick={() => {
                    setFormData({
                      ...formData,
                      optionalProducts: [...formData.optionalProducts, { ...initialLineItem }],
                    });
                  }}
                >
                  Add Optional Product
                </Button>
              </Box>
            )}

            {/* Other Info Tab */}
            {activeTab === 2 && (
              <Grid container spacing={4} sx={{ p: 2 }}>
                {/* Left Column */}
                <Grid item xs={12} md={6}>
                  {/* SALES Section */}
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333', mb: 2, borderBottom: '1px solid #e0e0e0', pb: 1 }}>
                    SALES
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <SalespersonSelect
                      value={formData.salesperson}
                      onChange={(salespersonId) => setFormData({ ...formData, salesperson: salespersonId })}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#666', fontSize: '13px' }}>Sales Team</Typography>
                      <Typography sx={{ fontSize: '14px', color: '#333' }}>{formData.salesTeam}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#666', fontSize: '13px' }}>Company</Typography>
                      <Typography sx={{ fontSize: '14px', color: '#333' }}>{formData.company}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#666', fontSize: '13px' }}>
                        Online signature <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                      </Typography>
                      <input
                        type="checkbox"
                        checked={formData.onlineSignature}
                        onChange={(e) => setFormData({ ...formData, onlineSignature: e.target.checked })}
                        style={{ accentColor: '#017e84' }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#666', fontSize: '13px' }}>
                        Online payment <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                      </Typography>
                      <input
                        type="checkbox"
                        checked={formData.onlinePayment}
                        onChange={(e) => setFormData({ ...formData, onlinePayment: e.target.checked })}
                        style={{ accentColor: '#017e84', marginRight: 8 }}
                      />
                      <Typography sx={{ fontSize: '13px', color: '#666' }}>of</Typography>
                      <TextField
                        size="small"
                        type="number"
                        value={formData.onlinePaymentPercent}
                        onChange={(e) => setFormData({ ...formData, onlinePaymentPercent: e.target.value })}
                        sx={{ width: 60, mx: 1 }}
                        inputProps={{ min: 0, max: 100 }}
                      />
                      <Typography sx={{ fontSize: '13px', color: '#666' }}>%</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#666', fontSize: '13px' }}>Customer Reference</Typography>
                      <TextField
                        size="small"
                        value={formData.customerReference}
                        onChange={(e) => setFormData({ ...formData, customerReference: e.target.value })}
                        sx={{ flex: 1 }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#666', fontSize: '13px' }}>Tags</Typography>
                      <TextField
                        size="small"
                        placeholder="Add tags..."
                        sx={{ flex: 1 }}
                      />
                    </Box>
                  </Box>

                  {/* DELIVERY Section */}
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333', mb: 2, borderBottom: '1px solid #e0e0e0', pb: 1 }}>
                    DELIVERY
                  </Typography>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#666', fontSize: '13px' }}>Shipping Weight</Typography>
                      <Typography sx={{ fontSize: '14px', color: '#333' }}>{formData.shippingWeight.toFixed(2)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#666', fontSize: '13px' }}>Warehouse</Typography>
                      <Typography sx={{ fontSize: '14px', color: '#333' }}>{formData.warehouse}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#666', fontSize: '13px' }}>
                        Incoterm <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                      </Typography>
                      <TextField
                        select
                        size="small"
                        value={formData.incoterm}
                        onChange={(e) => setFormData({ ...formData, incoterm: e.target.value })}
                        sx={{ flex: 1 }}
                      >
                        <MenuItem value="">Select...</MenuItem>
                        <MenuItem value="EXW">EXW - Ex Works</MenuItem>
                        <MenuItem value="FOB">FOB - Free On Board</MenuItem>
                        <MenuItem value="CIF">CIF - Cost, Insurance, Freight</MenuItem>
                        <MenuItem value="DAP">DAP - Delivered at Place</MenuItem>
                        <MenuItem value="DDP">DDP - Delivered Duty Paid</MenuItem>
                      </TextField>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#666', fontSize: '13px' }}>Incoterm Location</Typography>
                      <TextField
                        size="small"
                        value={formData.incotermLocation}
                        onChange={(e) => setFormData({ ...formData, incotermLocation: e.target.value })}
                        sx={{ flex: 1 }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#666', fontSize: '13px' }}>
                        Shipping Policy <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                      </Typography>
                      <Typography sx={{ fontSize: '14px', color: '#333' }}>As soon as possible</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#666', fontSize: '13px' }}>
                        Delivery Date <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                      </Typography>
                      <DatePicker
                        value={formData.deliveryDate}
                        onChange={(newValue) => setFormData({ ...formData, deliveryDate: newValue })}
                        slotProps={{ textField: { size: 'small', sx: { flex: 1 } } }}
                      />
                    </Box>
                  </Box>
                </Grid>

                {/* Right Column */}
                <Grid item xs={12} md={6}>
                  {/* INVOICING Section */}
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333', mb: 2, borderBottom: '1px solid #e0e0e0', pb: 1 }}>
                    INVOICING
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#666', fontSize: '13px' }}>
                        Fiscal Position <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                      </Typography>
                      <Typography sx={{ fontSize: '14px', color: '#333' }}>{formData.fiscalPosition}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#666', fontSize: '13px' }}>
                        Project <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                      </Typography>
                      <TextField
                        size="small"
                        value={formData.project}
                        onChange={(e) => setFormData({ ...formData, project: e.target.value })}
                        placeholder="Select..."
                        sx={{ flex: 1 }}
                      />
                    </Box>
                  </Box>

                  {/* TRACKING Section */}
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333', mb: 2, borderBottom: '1px solid #e0e0e0', pb: 1 }}>
                    TRACKING
                  </Typography>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#666', fontSize: '13px' }}>
                        Source Document <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                      </Typography>
                      <TextField
                        size="small"
                        value={formData.sourceDocument}
                        onChange={(e) => setFormData({ ...formData, sourceDocument: e.target.value })}
                        sx={{ flex: 1 }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#666', fontSize: '13px' }}>Opportunity</Typography>
                      <TextField
                        size="small"
                        value={formData.opportunity}
                        onChange={(e) => setFormData({ ...formData, opportunity: e.target.value })}
                        sx={{ flex: 1 }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#666', fontSize: '13px' }}>
                        Campaign <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                      </Typography>
                      <TextField
                        size="small"
                        value={formData.campaign}
                        onChange={(e) => setFormData({ ...formData, campaign: e.target.value })}
                        sx={{ flex: 1 }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#666', fontSize: '13px' }}>
                        Medium <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                      </Typography>
                      <TextField
                        size="small"
                        value={formData.medium}
                        onChange={(e) => setFormData({ ...formData, medium: e.target.value })}
                        sx={{ flex: 1 }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#666', fontSize: '13px' }}>
                        Source <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                      </Typography>
                      <TextField
                        size="small"
                        value={formData.source}
                        onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                        sx={{ flex: 1 }}
                      />
                    </Box>
                  </Box>
                </Grid>

                {/* Notes Section at bottom */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333', mb: 2, borderBottom: '1px solid #e0e0e0', pb: 1, mt: 2 }}>
                    NOTES
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Customer Notes"
                        name="customerNotes"
                        value={formData.customerNotes}
                        onChange={handleChange}
                        helperText="Visible to customer on the quotation"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Internal Notes"
                        name="internalNotes"
                        value={formData.internalNotes}
                        onChange={handleChange}
                        helperText="Internal use only"
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}

            {/* Attachments Tab */}
            {activeTab === 3 && (
              <Box sx={{ p: 2 }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  hidden
                  onChange={(e) => handleAttachmentFiles(e.target.files)}
                />

                <Box
                  onClick={() => !uploadingAttachments && fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onDrop={handleAttachmentDrop}
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    border: '2px dashed #c8a2c8',
                    borderRadius: 1,
                    backgroundColor: uploadingAttachments ? '#f5f5f5' : '#fbf7fb',
                    cursor: uploadingAttachments ? 'wait' : 'pointer',
                    transition: 'background-color 0.2s',
                    '&:hover': { backgroundColor: uploadingAttachments ? '#f5f5f5' : '#f3e8f3' },
                  }}
                >
                  <CloudUpload sx={{ fontSize: 40, color: '#875A7B', mb: 1 }} />
                  <Typography variant="body1" sx={{ color: '#333', fontWeight: 500 }}>
                    {uploadingAttachments ? 'Uploading…' : 'Click or drag files here to attach'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#888' }}>
                    Max 25 MB per file. PDF, Office docs, images, ZIP.
                  </Typography>
                  {!id && (
                    <Typography variant="caption" sx={{ display: 'block', color: '#875A7B', mt: 0.5 }}>
                      Files will upload automatically when you save the quotation.
                    </Typography>
                  )}
                  {uploadingAttachments && (
                    <LinearProgress
                      variant="determinate"
                      value={uploadProgress}
                      sx={{ mt: 2, borderRadius: 1 }}
                    />
                  )}
                </Box>

                {(attachments.length > 0 || pendingFiles.length > 0) ? (
                  <List sx={{ mt: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    {attachments.map((att, idx) => (
                      <ListItem
                        key={att._id || att.filename || `att-${idx}`}
                        divider={idx < attachments.length - 1 || pendingFiles.length > 0}
                        secondaryAction={
                          <Tooltip title="Remove">
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => handleRemoveAttachment(att._id)}
                              sx={{ color: '#d32f2f' }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        }
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {getFileIcon(att.mimeType)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Link
                              href={att.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              sx={{ fontSize: 14, color: '#017e84', fontWeight: 500 }}
                            >
                              {att.originalName || att.filename}
                            </Link>
                          }
                          secondary={
                            <Typography variant="caption" sx={{ color: '#888' }}>
                              {formatFileSize(att.size)}
                              {att.uploadedAt
                                ? ` • ${dayjs(att.uploadedAt).format('DD MMM YYYY HH:mm')}`
                                : ''}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}

                    {pendingFiles.map((p, idx) => (
                      <ListItem
                        key={p.localId}
                        divider={idx < pendingFiles.length - 1}
                        secondaryAction={
                          <Tooltip title="Remove from queue">
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => handleRemovePendingFile(p.localId)}
                              sx={{ color: '#d32f2f' }}
                            >
                              <Close fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        }
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {getFileIcon(p.type)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography sx={{ fontSize: 14, color: '#333', fontWeight: 500 }}>
                                {p.name}
                              </Typography>
                              <Chip
                                label="Pending"
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: 10,
                                  backgroundColor: '#fff3e0',
                                  color: '#e65100',
                                }}
                              />
                            </Box>
                          }
                          secondary={
                            <Typography variant="caption" sx={{ color: '#888' }}>
                              {formatFileSize(p.size)} • Uploads on save
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ mt: 2, color: '#888', textAlign: 'center' }}
                  >
                    No attachments yet.
                  </Typography>
                )}
              </Box>
            )}

            {/* Chatter Section - Message/Activity area */}
            <Divider sx={{ my: 3 }} />
            
            {/* Top Tab Bar - Send message / Log note / Activity */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: { xs: 0.5, sm: 1 }, 
              mb: 2,
              flexWrap: 'wrap'
            }}>
              <Button
                variant={activityTab === 0 ? 'contained' : 'text'}
                size="small"
                onClick={() => setActivityTab(0)}
                sx={{
                  backgroundColor: activityTab === 0 ? '#875A7B' : 'transparent',
                  color: activityTab === 0 ? 'white' : '#666',
                  textTransform: 'none',
                  fontWeight: 400,
                  fontSize: { xs: '12px', sm: '13px' },
                  px: { xs: 1.5, sm: 2 },
                  borderRadius: '4px',
                  '&:hover': { backgroundColor: activityTab === 0 ? '#714B68' : 'rgba(0,0,0,0.04)' },
                }}
              >
                Send message
              </Button>
              <Button
                variant={activityTab === 1 ? 'contained' : 'text'}
                size="small"
                onClick={() => {
                  if (!requireCustomer('log note')) return;
                  setActivityTab(1);
                }}
                sx={{
                  backgroundColor: activityTab === 1 ? '#666' : 'transparent',
                  color: activityTab === 1 ? 'white' : '#666',
                  textTransform: 'none',
                  fontWeight: 400,
                  fontSize: { xs: '12px', sm: '13px' },
                  px: { xs: 1.5, sm: 2 },
                  borderRadius: '4px',
                  '&:hover': { backgroundColor: activityTab === 1 ? '#555' : 'rgba(0,0,0,0.04)' },
                }}
              >
                Log note
              </Button>
              <Button
                variant={activityTab === 2 ? 'contained' : 'text'}
                size="small"
                onClick={() => {
                  if (!requireCustomer('activity')) return;
                  setActivityTab(2);
                }}
                sx={{
                  backgroundColor: activityTab === 2 ? '#666' : 'transparent',
                  color: activityTab === 2 ? 'white' : '#666',
                  textTransform: 'none',
                  fontWeight: 400,
                  fontSize: { xs: '12px', sm: '13px' },
                  px: { xs: 1.5, sm: 2 },
                  borderRadius: '4px',
                  '&:hover': { backgroundColor: activityTab === 2 ? '#555' : 'rgba(0,0,0,0.04)' },
                }}
              >
                Activity
              </Button>
              <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
              <IconButton size="small" sx={{ color: '#6c757d' }}>
                <Search sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton size="small" sx={{ color: '#6c757d' }}>
                <AttachFile sx={{ fontSize: 18 }} />
              </IconButton>
              <Box sx={{ display: 'flex', alignItems: 'center', color: '#6c757d', ml: 0.5 }}>
                <PersonAdd sx={{ fontSize: 18 }} />
                <Typography sx={{ fontSize: '12px', ml: 0.3 }}>0</Typography>
              </Box>
            </Box>

            {/* Activity Log Section */}
            <Box sx={{ mt: 3 }}>
              {/* Today Divider */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
                <Typography sx={{ px: 2, color: '#999', fontSize: '13px' }}>Today</Typography>
                <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
              </Box>
              
              {/* Activity Entry */}
              <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                <Avatar sx={{ width: 36, height: 36, backgroundColor: '#e91e63' }}>
                  {user?.name?.charAt(0) || 'A'}
                </Avatar>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                    {user?.name || 'Admin User'}{' '}
                    <Typography component="span" sx={{ color: '#999', fontWeight: 400, fontSize: '12px' }}>
                      {new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase()}
                    </Typography>
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', fontSize: '13px' }}>
                    Creating a new record...
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Payment Terms Search Dialog */}
      <Dialog 
        open={paymentTermSearchDialogOpen} 
        onClose={() => {
          setPaymentTermSearchDialogOpen(false);
          setPaymentTermSearchText('');
          setPaymentTermPage(0);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ 
          sx: { 
            maxHeight: '80vh',
            overflow: 'hidden',
          } 
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0', py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 500, color: '#333', fontSize: '16px' }}>
            Search: Payment Terms
          </Typography>
          <IconButton 
            onClick={() => {
              setPaymentTermSearchDialogOpen(false);
              setPaymentTermSearchText('');
              setPaymentTermPage(0);
            }}
            size="small"
          >
            <Close sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          {/* Search Bar */}
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, px: 3, borderBottom: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: 300, border: '1px solid #e0e0e0', borderRadius: 1, px: 1 }}>
              <Search sx={{ fontSize: 20, color: '#999' }} />
              <TextField
                placeholder="Search..."
                value={paymentTermSearchText}
                onChange={(e) => {
                  setPaymentTermSearchText(e.target.value);
                  setPaymentTermPage(0); // Reset to first page on search
                }}
                size="small"
                variant="standard"
                InputProps={{ disableUnderline: true }}
                sx={{ flex: 1, '& .MuiInputBase-input': { py: 0.75, px: 1 } }}
              />
              <KeyboardArrowDown sx={{ fontSize: 20, color: '#666' }} />
            </Box>
            <Typography sx={{ ml: 3, color: '#666', fontSize: '13px', alignSelf: 'center' }}>
              {filteredPaymentTerms.length > 0 ? `${paymentTermStartIndex}-${paymentTermEndIndex}` : '0'} / {filteredPaymentTerms.length}
            </Typography>
            <Box sx={{ ml: 1 }}>
              <IconButton 
                size="small" 
                disabled={paymentTermPage === 0}
                onClick={() => setPaymentTermPage(prev => Math.max(0, prev - 1))}
              >
                <NavigateBefore sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton 
                size="small" 
                disabled={paymentTermPage >= totalPaymentTermPages - 1}
                onClick={() => setPaymentTermPage(prev => Math.min(totalPaymentTermPages - 1, prev + 1))}
              >
                <NavigateNext sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Box>

          {/* Table Header */}
          <Box sx={{ display: 'flex', px: 3, py: 1.5, borderBottom: '1px solid #e0e0e0', backgroundColor: '#fafafa' }}>
            <Typography sx={{ flex: 1, fontWeight: 600, fontSize: '13px', color: '#333' }}>Payment Terms</Typography>
            <Typography sx={{ width: 200, fontWeight: 600, fontSize: '13px', color: '#333' }}>Company</Typography>
          </Box>

          {/* Payment Terms List */}
          <Box sx={{ 
            maxHeight: 400, 
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#d0d0d0',
              borderRadius: '3px',
              '&:hover': {
                backgroundColor: '#b0b0b0',
              },
            },
            scrollbarWidth: 'thin',
            scrollbarColor: '#d0d0d0 transparent',
          }}>
            {paginatedPaymentTerms.map((term) => (
              <Box
                key={term.id}
                onClick={() => handleSelectPaymentTerm(term.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 3,
                  py: 1.5,
                  borderBottom: '1px solid #f0f0f0',
                  cursor: 'pointer',
                  backgroundColor: formData.paymentTerms === term.id ? '#e8f5f5' : 'transparent',
                  '&:hover': { backgroundColor: '#f5f5f5' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', mr: 1, color: '#ccc' }}>
                    <Box sx={{ width: 4, height: 4, backgroundColor: '#ccc', borderRadius: '50%', mb: 0.25 }} />
                    <Box sx={{ width: 4, height: 4, backgroundColor: '#ccc', borderRadius: '50%', mb: 0.25 }} />
                    <Box sx={{ width: 4, height: 4, backgroundColor: '#ccc', borderRadius: '50%' }} />
                  </Box>
                  <Typography sx={{ fontSize: '13px', color: '#333' }}>{term.name}</Typography>
                  {term.isCustom && (
                    <Chip label="Custom" size="small" sx={{ ml: 1, height: 20, fontSize: '11px' }} />
                  )}
                </Box>
                <Typography sx={{ width: 200, fontSize: '13px', color: '#666' }}>
                  {term.company || ''}
                </Typography>
              </Box>
            ))}
            {paginatedPaymentTerms.length === 0 && (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography sx={{ color: '#666', fontSize: '13px' }}>
                  No payment terms found
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0', justifyContent: 'space-between' }}>
          <Button 
            variant="contained"
            onClick={() => {
              setPaymentTermSearchDialogOpen(false);
              setPaymentTermSearchText('');
              setPaymentTermPage(0);
            }}
            sx={{ 
              backgroundColor: '#875A7B',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#714B68' }
            }}
          >
            Close
          </Button>
          <Button 
            startIcon={<Add />}
            onClick={() => {
              setPaymentTermSearchDialogOpen(false);
              setPaymentTermDialogOpen(true);
            }}
            sx={{ color: '#017e84', textTransform: 'none' }}
          >
            New
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Payment Term Dialog */}
      <Dialog 
        open={paymentTermDialogOpen} 
        onClose={() => setPaymentTermDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0', py: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 500, color: '#333' }}>
            Create New Payment Term
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Payment Term Name"
              value={newPaymentTerm.name}
              onChange={(e) => setNewPaymentTerm({ ...newPaymentTerm, name: e.target.value })}
              fullWidth
              required
              placeholder="e.g., Net 60 Days"
              size="small"
            />
            <TextField
              label="Number of Days"
              type="number"
              value={newPaymentTerm.days}
              onChange={(e) => setNewPaymentTerm({ ...newPaymentTerm, days: e.target.value })}
              fullWidth
              placeholder="e.g., 60"
              size="small"
              inputProps={{ min: 0 }}
            />
            <TextField
              label="Description (Optional)"
              value={newPaymentTerm.description}
              onChange={(e) => setNewPaymentTerm({ ...newPaymentTerm, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              placeholder="Additional details about this payment term"
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button 
            onClick={() => {
              setPaymentTermDialogOpen(false);
              setNewPaymentTerm({ name: '', days: '', description: '' });
            }}
            sx={{ color: '#666' }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleAddCustomPaymentTerm}
            sx={{ 
              backgroundColor: '#875A7B',
              '&:hover': { backgroundColor: '#714B68' }
            }}
          >
            Create Payment Term
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Product Dialog */}
      <Dialog 
        open={productDialogOpen} 
        onClose={() => {
          setProductDialogOpen(false);
          setProductDialogIndex(null);
          setNewProduct({
            name: '', productType: 'goods', canBeSold: true, canBePurchased: true, isRental: false,
            trackInventory: false, invoicingPolicy: 'ordered', salesPrice: '', salesTax: '18',
            cost: '', purchaseTax: '18', category: '', reference: '', barcode: '', hsnCode: '',
            company: 'all', internalNotes: '',
          });
          setProductTab(0);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '4px',
            minHeight: '80vh',
          }
        }}
      >
        {/* Header */}
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: '1px solid #e0e0e0',
          py: 1.5,
          px: 2,
          backgroundColor: '#f8f8f8',
        }}>
          <Typography sx={{ fontWeight: 500, fontSize: '16px', color: '#333' }}>
            Create Product
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton size="small" sx={{ color: '#666' }}>
              <OpenInFull sx={{ fontSize: '18px' }} />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => {
                setProductDialogOpen(false);
                setProductDialogIndex(null);
                setNewProduct({
                  name: '', productType: 'goods', canBeSold: true, canBePurchased: true, isRental: false,
                  trackInventory: false, invoicingPolicy: 'ordered', salesPrice: '', salesTax: '18',
                  cost: '', purchaseTax: '18', category: '', reference: '', barcode: '', hsnCode: '',
                  company: 'all', internalNotes: '',
                });
                setProductTab(0);
              }}
              sx={{ color: '#666' }}
            >
              <Close sx={{ fontSize: '18px' }} />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {/* Main Form Area */}
          <Box sx={{ p: 3, flex: 1 }}>
            {/* Product Label + Name + Image */}
            <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '12px', color: '#666', mb: 0.5 }}>Product</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton size="small" sx={{ color: '#999' }}>
                    <StarBorder sx={{ fontSize: '24px' }} />
                  </IconButton>
                  <TextField
                    fullWidth
                    variant="standard"
                    value={newProduct.name}
                    onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                    placeholder="e.g. Cheese Burger"
                    InputProps={{
                      disableUnderline: true,
                      sx: { 
                        fontSize: '28px', 
                        fontWeight: 400,
                        color: '#333',
                        borderBottom: '1px solid #e0e0e0',
                        pb: 0.5,
                      }
                    }}
                  />
                </Box>
              </Box>
              
              {/* Product Image */}
              <Box sx={{ 
                width: 80, 
                height: 80, 
                backgroundColor: '#e8f4f4', 
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#d8eeee' }
              }}>
                <AddPhotoAlternate sx={{ fontSize: 40, color: '#00a09d' }} />
              </Box>
            </Box>
            
            {/* Sales / Purchase / Rental Checkboxes */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={newProduct.canBeSold} 
                    onChange={(e) => setNewProduct({ ...newProduct, canBeSold: e.target.checked })}
                    size="small"
                    sx={{ color: '#00a09d', '&.Mui-checked': { color: '#00a09d' } }}
                  />
                }
                label={<Typography sx={{ fontSize: '13px' }}>Sales</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={newProduct.canBePurchased} 
                    onChange={(e) => setNewProduct({ ...newProduct, canBePurchased: e.target.checked })}
                    size="small"
                    sx={{ color: '#00a09d', '&.Mui-checked': { color: '#00a09d' } }}
                  />
                }
                label={<Typography sx={{ fontSize: '13px' }}>Purchase</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={newProduct.isRental} 
                    onChange={(e) => setNewProduct({ ...newProduct, isRental: e.target.checked })}
                    size="small"
                    sx={{ color: '#00a09d', '&.Mui-checked': { color: '#00a09d' } }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '13px' }}>Rental</Typography>
                    <Tooltip title="Enable rental for this product">
                      <HelpOutline sx={{ fontSize: '14px', color: '#00a09d' }} />
                    </Tooltip>
                  </Box>
                }
              />
            </Box>
            
            {/* Tabs */}
            <Tabs
              value={productTab}
              onChange={(e, newValue) => setProductTab(newValue)}
              sx={{
                minHeight: '36px',
                borderBottom: '1px solid #e0e0e0',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '13px',
                  minHeight: '36px',
                  py: 0.5,
                  px: 2,
                  color: '#666',
                  '&.Mui-selected': { color: '#333', fontWeight: 500 },
                },
                '& .MuiTabs-indicator': { backgroundColor: '#333' },
              }}
            >
              <Tab label="General Information" />
              <Tab label="Sales" />
              <Tab label="Purchase" />
              <Tab label="Inventory" />
            </Tabs>
            
            {/* Tab Content */}
            <Box sx={{ pt: 2 }}>
              {productTab === 0 && (
                <Grid container spacing={4}>
                  {/* Left Column */}
                  <Grid item xs={12} md={6}>
                    {/* Product Type */}
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                      <Box sx={{ width: '130px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '13px', color: '#333' }}>Product Type</Typography>
                        <Tooltip title="Type of the product">
                          <HelpOutline sx={{ fontSize: '14px', color: '#00a09d' }} />
                        </Tooltip>
                      </Box>
                      <RadioGroup
                        row
                        value={newProduct.productType}
                        onChange={(e) => setNewProduct({ ...newProduct, productType: e.target.value })}
                      >
                        <FormControlLabel 
                          value="goods" 
                          control={<Radio size="small" sx={{ color: '#00a09d', '&.Mui-checked': { color: '#00a09d' } }} />} 
                          label={<Typography sx={{ fontSize: '13px' }}>Goods</Typography>}
                        />
                        <FormControlLabel 
                          value="service" 
                          control={<Radio size="small" sx={{ color: '#00a09d', '&.Mui-checked': { color: '#00a09d' } }} />} 
                          label={<Typography sx={{ fontSize: '13px' }}>Service</Typography>}
                        />
                        <FormControlLabel 
                          value="combo" 
                          control={<Radio size="small" sx={{ color: '#00a09d', '&.Mui-checked': { color: '#00a09d' } }} />} 
                          label={<Typography sx={{ fontSize: '13px' }}>Combo</Typography>}
                        />
                      </RadioGroup>
                    </Box>
                    
                    {/* Track Inventory */}
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                      <Box sx={{ width: '130px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '13px', color: '#333' }}>Track Inventory</Typography>
                        <Tooltip title="Track stock levels for this product">
                          <HelpOutline sx={{ fontSize: '14px', color: '#00a09d' }} />
                        </Tooltip>
                      </Box>
                      <Checkbox 
                        checked={newProduct.trackInventory} 
                        onChange={(e) => setNewProduct({ ...newProduct, trackInventory: e.target.checked })}
                        size="small"
                        sx={{ color: '#00a09d', '&.Mui-checked': { color: '#00a09d' }, p: 0 }}
                      />
                    </Box>
                    
                    {/* Invoicing Policy */}
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                      <Box sx={{ width: '130px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '13px', color: '#333' }}>Invoicing Policy</Typography>
                        <Tooltip title="Quantities invoiced: Ordered or Delivered">
                          <HelpOutline sx={{ fontSize: '14px', color: '#00a09d' }} />
                        </Tooltip>
                      </Box>
                      <Typography sx={{ fontSize: '13px', color: '#666' }}>Ordered quantities</Typography>
                    </Box>
                  </Grid>
                  
                  {/* Right Column */}
                  <Grid item xs={12} md={6}>
                    {/* Sales Price */}
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                      <Box sx={{ width: '130px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '13px', color: '#333' }}>Sales Price</Typography>
                        <Tooltip title="Price at which the product is sold">
                          <HelpOutline sx={{ fontSize: '14px', color: '#00a09d' }} />
                        </Tooltip>
                      </Box>
                      <TextField
                        variant="standard"
                        value={newProduct.salesPrice}
                        onChange={(e) => setNewProduct({ ...newProduct, salesPrice: e.target.value })}
                        placeholder="0.00"
                        type="number"
                        InputProps={{
                          disableUnderline: true,
                          startAdornment: <InputAdornment position="start" sx={{ mr: 0.5 }}>₹</InputAdornment>,
                          sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                        }}
                        sx={{ width: '150px' }}
                      />
                    </Box>
                    
                    {/* Sales Taxes */}
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                      <Box sx={{ width: '130px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '13px', color: '#333' }}>Sales Taxes</Typography>
                        <Tooltip title="Default taxes for sales">
                          <HelpOutline sx={{ fontSize: '14px', color: '#00a09d' }} />
                        </Tooltip>
                      </Box>
                      <TextField
                        variant="standard"
                        type="number"
                        value={newProduct.salesTax}
                        onChange={(e) => setNewProduct({ ...newProduct, salesTax: e.target.value })}
                        InputProps={{
                          disableUnderline: true,
                          endAdornment: <InputAdornment position="end" sx={{ ml: 0.5 }}>% GST</InputAdornment>,
                          inputProps: { min: 0, max: 100, step: 0.5 },
                          sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 },
                        }}
                        sx={{ width: '120px' }}
                      />
                    </Box>
                    
                    {/* Cost */}
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                      <Box sx={{ width: '130px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '13px', color: '#333' }}>Cost</Typography>
                        <Tooltip title="Cost price of the product">
                          <HelpOutline sx={{ fontSize: '14px', color: '#00a09d' }} />
                        </Tooltip>
                      </Box>
                      <TextField
                        variant="standard"
                        value={newProduct.cost}
                        onChange={(e) => setNewProduct({ ...newProduct, cost: e.target.value })}
                        placeholder="0.00"
                        type="number"
                        InputProps={{
                          disableUnderline: true,
                          startAdornment: <InputAdornment position="start" sx={{ mr: 0.5 }}>₹</InputAdornment>,
                          sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                        }}
                        sx={{ width: '150px' }}
                      />
                    </Box>
                    
                    {/* Purchase Taxes */}
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                      <Box sx={{ width: '130px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '13px', color: '#333' }}>Purchase Taxes</Typography>
                        <Tooltip title="Default taxes for purchases">
                          <HelpOutline sx={{ fontSize: '14px', color: '#00a09d' }} />
                        </Tooltip>
                      </Box>
                      <TextField
                        variant="standard"
                        type="number"
                        value={newProduct.purchaseTax}
                        onChange={(e) => setNewProduct({ ...newProduct, purchaseTax: e.target.value })}
                        InputProps={{
                          disableUnderline: true,
                          endAdornment: <InputAdornment position="end" sx={{ ml: 0.5 }}>% GST</InputAdornment>,
                          inputProps: { min: 0, max: 100, step: 0.5 },
                          sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 },
                        }}
                        sx={{ width: '120px' }}
                      />
                    </Box>
                    
                    {/* Category */}
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                      <Typography sx={{ width: '130px', fontSize: '13px', color: '#333' }}>Category</Typography>
                      <TextField
                        select
                        variant="standard"
                        value={newProduct.category}
                        onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
                        InputProps={{
                          disableUnderline: true,
                          sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                        }}
                        sx={{ flex: 1, minWidth: 150 }}
                        SelectProps={{
                          displayEmpty: true,
                          renderValue: (value) => value ? {
                            hardware: 'Hardware',
                            software: 'Software',
                            service: 'Services',
                            subscription: 'Subscriptions',
                            consulting: 'Consulting',
                            support: 'Support',
                            training: 'Training',
                            other: 'Other'
                          }[value] || value : <span style={{ color: '#999' }}>Select category...</span>
                        }}
                      >
                        <MenuItem value="hardware">Hardware</MenuItem>
                        <MenuItem value="software">Software</MenuItem>
                        <MenuItem value="service">Services</MenuItem>
                        <MenuItem value="subscription">Subscriptions</MenuItem>
                        <MenuItem value="consulting">Consulting</MenuItem>
                        <MenuItem value="support">Support</MenuItem>
                        <MenuItem value="training">Training</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </TextField>
                    </Box>
                    
                    {/* Reference */}
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                      <Typography sx={{ width: '130px', fontSize: '13px', color: '#333' }}>Reference</Typography>
                      <TextField
                        variant="standard"
                        value={newProduct.reference}
                        onChange={(e) => setNewProduct({ ...newProduct, reference: e.target.value })}
                        placeholder="Internal reference..."
                        InputProps={{
                          disableUnderline: true,
                          sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                        }}
                        sx={{ flex: 1 }}
                      />
                    </Box>
                    
                    {/* Barcode */}
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                      <Typography sx={{ width: '130px', fontSize: '13px', color: '#333' }}>Barcode</Typography>
                      <TextField
                        variant="standard"
                        value={newProduct.barcode}
                        onChange={(e) => setNewProduct({ ...newProduct, barcode: e.target.value })}
                        placeholder="Barcode..."
                        InputProps={{
                          disableUnderline: true,
                          sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                        }}
                        sx={{ flex: 1 }}
                      />
                    </Box>
                    
                    {/* HSN/SAC Code */}
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                      <Box sx={{ width: '130px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '13px', color: '#333' }}>HSN/SAC Code</Typography>
                        <Tooltip title="Harmonized System of Nomenclature / Service Accounting Code">
                          <HelpOutline sx={{ fontSize: '14px', color: '#00a09d' }} />
                        </Tooltip>
                      </Box>
                      <TextField
                        variant="standard"
                        value={newProduct.hsnCode}
                        onChange={(e) => setNewProduct({ ...newProduct, hsnCode: e.target.value })}
                        placeholder="HSN/SAC Code..."
                        InputProps={{
                          disableUnderline: true,
                          sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                        }}
                        sx={{ flex: 1 }}
                      />
                    </Box>
                    
                    {/* Company */}
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                      <Typography sx={{ width: '130px', fontSize: '13px', color: '#333' }}>Company</Typography>
                      <Typography sx={{ fontSize: '13px', color: '#999' }}>Visible to all</Typography>
                    </Box>
                  </Grid>
                </Grid>
              )}
              
              {productTab === 1 && (
                <Typography sx={{ fontSize: '13px', color: '#666', py: 2 }}>
                  Sales settings will be available after creating the product.
                </Typography>
              )}
              
              {productTab === 2 && (
                <Typography sx={{ fontSize: '13px', color: '#666', py: 2 }}>
                  Purchase settings will be available after creating the product.
                </Typography>
              )}
              
              {productTab === 3 && (
                <Typography sx={{ fontSize: '13px', color: '#666', py: 2 }}>
                  Inventory settings will be available after creating the product.
                </Typography>
              )}
            </Box>
          </Box>
          
          {/* Internal Notes Section */}
          <Box sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#333', textTransform: 'uppercase' }}>
                Internal Notes
              </Typography>
              <IconButton size="small" sx={{ ml: 'auto', color: '#666' }}>
                <ExpandMore sx={{ fontSize: '18px' }} />
              </IconButton>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              value={newProduct.internalNotes}
              onChange={(e) => setNewProduct({ ...newProduct, internalNotes: e.target.value })}
              placeholder="Internal notes..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '13px',
                  '& fieldset': { borderColor: '#e0e0e0' },
                }
              }}
            />
          </Box>
        </DialogContent>
        
        {/* Footer */}
        <DialogActions sx={{ 
          px: 2, 
          py: 1.5, 
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f8f8f8',
          justifyContent: 'flex-start',
        }}>
          <Button
            variant="contained"
            onClick={handleAddCustomProduct}
            sx={{
              backgroundColor: '#00a09d',
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 500,
              px: 2,
              '&:hover': { backgroundColor: '#008f8c' },
            }}
          >
            Save
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setProductDialogOpen(false);
              setProductDialogIndex(null);
              setNewProduct({
                name: '', productType: 'goods', canBeSold: true, canBePurchased: true, isRental: false,
                trackInventory: false, invoicingPolicy: 'ordered', salesPrice: '', salesTax: '18',
                cost: '', purchaseTax: '18', category: '', reference: '', barcode: '', hsnCode: '',
                company: 'all', internalNotes: '',
              });
              setProductTab(0);
            }}
            sx={{
              backgroundColor: '#4a4a4a',
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 500,
              px: 2,
              '&:hover': { backgroundColor: '#333' },
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Customer Dialog */}
      <Dialog 
        open={customerDialogOpen} 
        onClose={() => {
          setCustomerDialogOpen(false);
          setNewCustomer({
            type: 'person', name: '', email: '', phone: '', company: '',
            street: '', city: '', zip: '', state: '', country: 'India', gstin: '',
          });
          setCustomerTab(0);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '4px',
            minHeight: '80vh',
          }
        }}
      >
        {/* Header */}
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: '1px solid #e0e0e0',
          py: 1.5,
          px: 2,
          backgroundColor: '#f8f8f8',
        }}>
          <Typography sx={{ fontWeight: 500, fontSize: '16px', color: '#333' }}>
            Create Customer
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton size="small" sx={{ color: '#666' }}>
              <OpenInFull sx={{ fontSize: '18px' }} />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => {
                setCustomerDialogOpen(false);
                setNewCustomer({
                  type: 'person', name: '', email: '', phone: '', company: '',
                  street: '', city: '', zip: '', state: '', country: 'India', gstin: '',
                });
                setCustomerTab(0);
              }}
              sx={{ color: '#666' }}
            >
              <Close sx={{ fontSize: '18px' }} />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {/* Main Form Area */}
          <Box sx={{ p: 3, flex: 1 }}>
            {/* Top Section: Avatar + Type + Name + Contact */}
            <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
              {/* Avatar */}
              <Box sx={{ 
                width: 100, 
                height: 100, 
                backgroundColor: '#f5f0f0', 
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Person sx={{ fontSize: 60, color: '#d4a5a5' }} />
              </Box>
              
              {/* Name & Contact */}
              <Box sx={{ flex: 1 }}>
                {/* Person/Company Toggle */}
                <RadioGroup
                  row
                  value={newCustomer.type}
                  onChange={(e) => setNewCustomer({ ...newCustomer, type: e.target.value })}
                  sx={{ mb: 1 }}
                >
                  <FormControlLabel 
                    value="person" 
                    control={<Radio size="small" sx={{ color: '#00a09d', '&.Mui-checked': { color: '#00a09d' } }} />} 
                    label={<Typography sx={{ fontSize: '13px' }}>Person</Typography>}
                  />
                  <FormControlLabel 
                    value="company" 
                    control={<Radio size="small" sx={{ color: '#00a09d', '&.Mui-checked': { color: '#00a09d' } }} />} 
                    label={<Typography sx={{ fontSize: '13px' }}>Company</Typography>}
                  />
                </RadioGroup>
                
                {/* Name Field */}
                <TextField
                  fullWidth
                  variant="standard"
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                  placeholder="e.g. Brandon Freeman"
                  InputProps={{
                    disableUnderline: true,
                    sx: { 
                      fontSize: '28px', 
                      fontWeight: 400,
                      color: '#333',
                      '&::placeholder': { color: '#999', opacity: 1 }
                    }
                  }}
                  sx={{ mb: 1 }}
                />
                
                {/* Email & Phone */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                    <Email sx={{ fontSize: '16px', color: '#666' }} />
                    <TextField
                      fullWidth
                      variant="standard"
                      value={newCustomer.email}
                      onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                      placeholder="Email"
                      InputProps={{
                        disableUnderline: true,
                        sx: { fontSize: '14px', color: '#666' }
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                    <Phone sx={{ fontSize: '16px', color: '#666' }} />
                    <TextField
                      fullWidth
                      variant="standard"
                      value={newCustomer.phone}
                      onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                      placeholder="Phone"
                      InputProps={{
                        disableUnderline: true,
                        sx: { fontSize: '14px', color: '#666' }
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
            
            {/* Two Column Layout */}
            <Grid container spacing={3}>
              {/* Left Column */}
              <Grid item xs={12} md={6}>
                {/* Company */}
                <Box sx={{ display: 'flex', mb: 2 }}>
                  <Typography sx={{ width: '100px', fontSize: '13px', color: '#333', pt: 1 }}>Company</Typography>
                  <TextField
                    fullWidth
                    variant="standard"
                    value={newCustomer.company}
                    onChange={(e) => setNewCustomer({ ...newCustomer, company: e.target.value })}
                    placeholder="Company Name..."
                    InputProps={{
                      disableUnderline: true,
                      sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                    }}
                  />
                </Box>
                
                {/* Address */}
                <Box sx={{ display: 'flex', mb: 1 }}>
                  <Typography sx={{ width: '100px', fontSize: '13px', color: '#333', pt: 1 }}>Address</Typography>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      variant="standard"
                      value={newCustomer.street}
                      onChange={(e) => setNewCustomer({ ...newCustomer, street: e.target.value })}
                      placeholder="Street..."
                      InputProps={{
                        disableUnderline: true,
                        sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5, mb: 1 }
                      }}
                    />
                    <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                      <TextField
                        select
                        variant="standard"
                        value={newCustomer.city}
                        onChange={(e) => setNewCustomer({ ...newCustomer, city: e.target.value })}
                        sx={{ flex: 1 }}
                        InputProps={{
                          disableUnderline: true,
                          sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                        }}
                      >
                        <MenuItem value="">Select City</MenuItem>
                        {cityOptions.map((city) => (
                          <MenuItem key={city} value={city}>{city}</MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        variant="standard"
                        value={newCustomer.zip}
                        onChange={(e) => setNewCustomer({ ...newCustomer, zip: e.target.value })}
                        placeholder="ZIP"
                        sx={{ width: '80px' }}
                        InputProps={{
                          disableUnderline: true,
                          sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                        }}
                      />
                      <TextField
                        variant="standard"
                        value={newCustomer.state}
                        onChange={(e) => setNewCustomer({ ...newCustomer, state: e.target.value })}
                        placeholder="State"
                        sx={{ flex: 1 }}
                        InputProps={{
                          disableUnderline: true,
                          sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                        }}
                      />
                    </Box>
                    <TextField
                      fullWidth
                      select
                      variant="standard"
                      value={newCustomer.country}
                      onChange={(e) => setNewCustomer({ ...newCustomer, country: e.target.value })}
                      InputProps={{
                        disableUnderline: true,
                        sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                      }}
                    >
                      {countryOptions.map((country) => (
                        <MenuItem key={country} value={country}>{country}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </Box>
              </Grid>
              
              {/* Right Column */}
              <Grid item xs={12} md={6}>
                {/* GSTIN */}
                <Box sx={{ display: 'flex', mb: 2 }}>
                  <Box sx={{ width: '120px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '13px', color: '#333' }}>GSTIN</Typography>
                    <Tooltip title="Goods and Services Tax Identification Number">
                      <HelpOutline sx={{ fontSize: '14px', color: '#00a09d', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                  <TextField
                    fullWidth
                    variant="standard"
                    value={newCustomer.gstin}
                    onChange={(e) => setNewCustomer({ ...newCustomer, gstin: e.target.value })}
                    placeholder="not applicable"
                    InputProps={{
                      disableUnderline: true,
                      sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
          
          {/* Tabs Section */}
          <Box sx={{ borderTop: '1px solid #e0e0e0' }}>
            <Tabs
              value={customerTab}
              onChange={(e, newValue) => setCustomerTab(newValue)}
              sx={{
                minHeight: '40px',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '13px',
                  minHeight: '40px',
                  py: 1,
                  px: 2,
                  color: '#666',
                  '&.Mui-selected': { color: '#333', fontWeight: 500 },
                },
                '& .MuiTabs-indicator': { backgroundColor: '#333' },
              }}
            >
              <Tab label="Contacts" />
              <Tab label="Sales & Purchase" />
              <Tab label="Invoicing" />
              <Tab label="Notes" />
            </Tabs>
            
            {/* Tab Content */}
            <Box sx={{ p: 2, minHeight: '100px', backgroundColor: '#fafafa' }}>
              {customerTab === 0 && (
                <Box sx={{ 
                  border: '1px dashed #ccc', 
                  borderRadius: '4px', 
                  p: 2, 
                  textAlign: 'center',
                  backgroundColor: '#fff',
                }}>
                  <Link
                    component="button"
                    sx={{ 
                      color: '#00a09d', 
                      textDecoration: 'none', 
                      fontSize: '13px',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    Add Contact
                  </Link>
                </Box>
              )}
              {customerTab === 1 && (
                <Typography sx={{ fontSize: '13px', color: '#666' }}>
                  Sales & Purchase settings will be available after creating the customer.
                </Typography>
              )}
              {customerTab === 2 && (
                <Typography sx={{ fontSize: '13px', color: '#666' }}>
                  Invoicing settings will be available after creating the customer.
                </Typography>
              )}
              {customerTab === 3 && (
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  placeholder="Internal notes..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '13px',
                      backgroundColor: '#fff',
                    }
                  }}
                />
              )}
            </Box>
          </Box>
        </DialogContent>
        
        {/* Footer */}
        <DialogActions sx={{ 
          px: 2, 
          py: 1.5, 
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f8f8f8',
          justifyContent: 'flex-start',
        }}>
          <Button
            variant="contained"
            onClick={handleAddCustomCustomer}
            sx={{
              backgroundColor: '#00a09d',
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 500,
              px: 2,
              '&:hover': { backgroundColor: '#008f8c' },
            }}
          >
            Save
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setCustomerDialogOpen(false);
              setNewCustomer({
                type: 'person', name: '', email: '', phone: '', company: '', jobPosition: '',
                street: '', street2: '', city: '', zip: '', state: '', country: 'India',
                gstTreatment: '', gstin: '', pan: '', website: '', tags: '',
              });
              setCustomerTab(0);
            }}
            sx={{
              backgroundColor: '#4a4a4a',
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 500,
              px: 2,
              '&:hover': { backgroundColor: '#333' },
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default QuotationBuilder;
